import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "./axiosConfig";
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  Coffee,
  Users,
  User,
  Info,
  Star,
  MessageCircle,
  X,
  Send,
  Play,
  Pause,
  RefreshCw,
  ExternalLink,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Edit,
  Search,
  Zap,
  Loader,
  AlertCircle,
  Target,
  TrendingUp,
  Copy,
  Sparkles,
  Lightbulb,
  Plus,
  Clock,
  Twitter,
  Globe,
  Calendar,
  Filter,
  ArrowRight,
  Award,
  Eye,
  Menu,
  EyeOff,
  ThumbsUp,
  HelpCircle,
  UserCheck,
  FileText,
  Layout,
  BarChart2,
  ArrowUp,
  Check,
  Lock,
  Heart,
  Repeat,
  MessageSquare,
  Briefcase,
  Linkedin,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import AnalysisProgressModal from "./AnalysisProgressModal";
import UpgradeModal from "./UpgradeModal";
// this is reddit card
import OpportunityCard from "./OpportunityCard";
import TwitterCard from "./TwitterCard";
import EngagedLeadCard from "./EngagedLeadCard";
import SearchComponent from "./Search";
import EnhancedHeader from "./EnhancedHeader";
import LinkedInCard from "./LinkedinCard";
import LinkedInFilterSection from "./LinkedInFilterSection";

// outside your GreenMarketingTab component for refresh issues:

const KeywordsInput = ({ value = [], onChange, maxKeywords, description, isEdit = false, handleUpgradeClick }) => {
  // State management
  const [inputValue, setInputValue] = useState("");
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [keywordError, setKeywordError] = useState(null);
  const [hasShownSuggestions, setHasShownSuggestions] = useState(false);

  // Automatically suggest keywords when description changes and we haven't shown suggestions yet
  useEffect(() => {
    if (description && description.length > 100 && value.length === 0 && !hasShownSuggestions && !isEdit) {
      getKeywordSuggestions();
      setHasShownSuggestions(true);
    }
  }, [description, isEdit]);

  // Function to handle keyboard input
  const handleKeyDown = (e) => {
    if (e.key === "," || e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      addKeyword();
    }
  };

  // Add keyword from input
  const addKeyword = () => {
    const trimmedValue = inputValue.trim();

    if (!trimmedValue || trimmedValue === ",") {
      setInputValue("");
      return;
    }

    const newKeywords = trimmedValue
      .split(",")
      .map((k) => k.trim())
      .filter((k) => k && !value.includes(k));

    if (value.length + newKeywords.length > maxKeywords) {
      setKeywordError(`You can only add up to ${maxKeywords} keywords based on your plan.`);
      setTimeout(() => setKeywordError(null), 3000);
      return;
    }

    if (newKeywords.length) {
      onChange([...value, ...newKeywords]);
      setKeywordError(null);
    }

    setInputValue("");
  };

  // Remove keyword
  const removeKeyword = (keyword) => {
    onChange(value.filter((k) => k !== keyword));
  };

  // Handle paste event for multiple keywords
  const handlePaste = (e) => {
    const pasteText = e.clipboardData.getData("text");

    if (pasteText.includes(",")) {
      e.preventDefault();

      const pasteKeywords = pasteText
        .split(",")
        .map((k) => k.trim())
        .filter((k) => k && !value.includes(k));

      if (value.length + pasteKeywords.length > maxKeywords) {
        setKeywordError(`You can only add up to ${maxKeywords} keywords based on your plan.`);
        setTimeout(() => setKeywordError(null), 3000);
        return;
      }

      onChange([...value, ...pasteKeywords]);
    }
  };

  // Get keyword suggestions from the backend
  const getKeywordSuggestions = async () => {
    if (!description || description.trim().length < 10) {
      setKeywordError("Please add a detailed product description first");
      setTimeout(() => setKeywordError(null), 3000);
      return;
    }

    setIsSuggesting(true);
    setKeywordError(null);

    try {
      const response = await axios.post("suggest-keywords/", {
        description: description,
      });

      if (response.data && response.data.keywords) {
        const newSuggestions = response.data.keywords.filter((keyword) => !value.includes(keyword));

        if (newSuggestions.length === 0) {
          setKeywordError("All suggested keywords are already added");
          setShowSuggestions(false);
          setTimeout(() => setKeywordError(null), 3000);
        } else {
          setSuggestions(newSuggestions);
          setShowSuggestions(true);
        }
      }
    } catch (error) {
      console.error("Error suggesting keywords:", error);
      setKeywordError("Failed to generate keyword suggestions. Please try again.");
      setTimeout(() => setKeywordError(null), 3000);
    } finally {
      setIsSuggesting(false);
    }
  };

  // Add a suggestion to keywords
  const addSuggestion = (suggestion) => {
    if (value.length >= maxKeywords) {
      setKeywordError(`You can only add up to ${maxKeywords} keywords based on your plan.`);
      setTimeout(() => setKeywordError(null), 3000);
      return;
    }

    onChange([...value, suggestion]);
    setSuggestions(suggestions.filter((s) => s !== suggestion));

    // If no more suggestions, hide the panel
    if (suggestions.length <= 1) {
      setShowSuggestions(false);
    }
  };

  // Clear all suggestions
  const clearSuggestions = () => {
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Add all suggestions (up to limit)
  const addAllSuggestions = () => {
    const availableSlots = maxKeywords - value.length;
    if (availableSlots <= 0) {
      setKeywordError(`You've reached your limit of ${maxKeywords} keywords.`);
      setTimeout(() => setKeywordError(null), 3000);
      return;
    }

    const suggestionsToAdd = suggestions.slice(0, availableSlots);
    onChange([...value, ...suggestionsToAdd]);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div className="space-y-2">
      {/* Display selected keywords as chips */}
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map((keyword, index) => (
          <div key={index} className="bg-green-100 text-green-800 px-2 py-1 rounded-md flex items-center animate-fadeIn">
            <span>{keyword}</span>
            <button
              type="button"
              onClick={() => removeKeyword(keyword)}
              className="ml-1 text-green-700 hover:text-green-900"
              aria-label={`Remove ${keyword}`}
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>

      {/* Keyword input field */}
      <div className="relative">
        <input
          type="text"
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder={value.length === 0 ? "Type keywords and press Enter or comma to add" : "Add more keywords..."}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={addKeyword}
          onPaste={handlePaste}
          disabled={value.length >= maxKeywords}
        />

        {value.length >= maxKeywords && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <span className="text-orange-500 text-sm flex items-center">
              <AlertCircle size={14} className="mr-1" />
              Limit reached
            </span>
          </div>
        )}
      </div>

      {/* Usage count and suggestion button */}
      <div className="flex flex-wrap justify-between items-center gap-2">
        <p className="text-xs text-gray-500">
          {value.length}/{maxKeywords} keywords used. Press Enter, Tab or comma (,) to add.
        </p>

        {/* Only show suggestion button if there's a description */}
        {description && description.length >= 10 && (
          <button
            type="button"
            onClick={getKeywordSuggestions}
            disabled={isSuggesting || value.length >= maxKeywords}
            className={`text-sm flex items-center space-x-1 px-3 py-1 rounded-lg transition-colors ${
              isSuggesting
                ? "bg-gray-100 text-gray-500 cursor-wait"
                : value.length >= maxKeywords
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-blue-50 text-blue-600 hover:bg-blue-100"
            }`}
          >
            {isSuggesting ? (
              <>
                <Loader size={14} className="animate-spin" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <Sparkles size={14} />
                <span>Suggest Keywords</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Error message */}
      {keywordError && (
        <div className="text-red-600 text-xs flex items-center mt-1 animate-fadeIn">
          <AlertCircle size={12} className="mr-1 flex-shrink-0" />
          <span>{keywordError}</span>
        </div>
      )}

      {/* Suggestions panel */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="mt-4 bg-blue-50 rounded-lg p-4 border border-blue-100 animate-fadeIn">
          <div className="flex justify-between items-center mb-3">
            <h4 className="text-sm font-medium text-blue-800 flex items-center">
              <Lightbulb size={16} className="mr-2 text-blue-600" />
              AI-Suggested Keywords
            </h4>
            <div className="flex space-x-2">
              <button
                onClick={addAllSuggestions}
                className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition-colors"
                disabled={maxKeywords - value.length <= 0}
              >
                Add All
              </button>
              <button
                onClick={clearSuggestions}
                className="text-xs bg-white text-gray-600 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => addSuggestion(suggestion)}
                disabled={value.length >= maxKeywords}
                className={`bg-white border border-blue-200 text-blue-800 px-2 py-1 rounded-md flex items-center hover:bg-blue-50 transition-colors ${
                  value.length >= maxKeywords ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <Plus size={12} className="mr-1 text-blue-500" />
                <span>{suggestion}</span>
              </button>
            ))}
          </div>

          {value.length >= maxKeywords && (
            <p className="text-xs text-orange-600 mt-2 flex items-center">
              <AlertCircle size={12} className="mr-1" />
              <span>
                You've reached your keyword limit ({maxKeywords}).
                <button onClick={handleUpgradeClick} className="ml-1 text-blue-600 hover:underline">
                  Upgrade for more
                </button>
              </span>
            </p>
          )}
        </div>
      )}

      {/* Helpful tips section */}
      <div className="mt-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
        <div className="flex items-start">
          <HelpCircle size={16} className="text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-gray-600">
              <span className="font-medium">Pro tip:</span> Add specific keywords that potential customers would use when looking for your
              product. Include problem statements and industry terms.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const StableProductModal = React.memo(
  ({
    isOpen,
    onClose,
    initialData = { name: "", url: "", description: "", keywords: [] },
    onSave,
    isEdit,
    isSaving,
    keywordLimit,
    userPlan,
    handleUpgradeClick,
  }) => {
    // State management
    const [localData, setLocalData] = useState(initialData);
    const [validationErrors, setValidationErrors] = useState({});
    const [formTouched, setFormTouched] = useState(false);
    const [activeStep, setActiveStep] = useState(1);
    const [animationDirection, setAnimationDirection] = useState(1); // 1 = forward, -1 = backward
    const [descriptionFocused, setDescriptionFocused] = useState(false);
    const [showAdvancedTips, setShowAdvancedTips] = useState(false);

    // Progress tracking
    const totalSteps = 3;
    const getStepName = (step) => {
      switch (step) {
        case 1:
          return "Basic Information";
        case 2:
          return "Product Description";
        case 3:
          return "Keywords";
        default:
          return "";
      }
    };

    // Reset local state when initialData changes
    useEffect(() => {
      setLocalData({
        ...initialData,
        keywords: Array.isArray(initialData.keywords) ? initialData.keywords : [],
      });

      // If editing, skip to relevant step based on what's missing
      if (isEdit) {
        if (!initialData.name || !initialData.url) {
          setActiveStep(1);
        } else if (!initialData.description || initialData.description.length < 50) {
          setActiveStep(2);
        } else {
          setActiveStep(3);
        }
      }
    }, [initialData, isEdit]);

    // Handle field changes
    const handleChange = (field) => (e) => {
      setLocalData({ ...localData, [field]: e.target.value });
      setFormTouched(true);

      // Clear validation error when field is changed
      if (validationErrors[field]) {
        setValidationErrors({ ...validationErrors, [field]: null });
      }
    };

    // Handle keywords changes
    const handleKeywordsChange = (newKeywords) => {
      setLocalData({ ...localData, keywords: newKeywords });
      setFormTouched(true);

      // Clear validation error when keywords are changed
      if (validationErrors.keywords) {
        setValidationErrors({ ...validationErrors, keywords: null });
      }
    };

    // Form validation
    const validateForm = () => {
      const errors = {};

      // Validate based on current step
      if (activeStep === 1) {
        if (!localData.name?.trim()) {
          errors.name = "Product name is required";
        }

        if (!localData.url?.trim()) {
          errors.url = "Product URL is required";
        } else if (!/^https?:\/\//i.test(localData.url)) {
          errors.url = "URL must start with http:// or https://";
        }
      }

      if (activeStep === 2) {
        if (!localData.description?.trim()) {
          errors.description = "Product description is required";
        } else if (localData.description.trim().length < 50) {
          errors.description = "Description should be at least 50 characters";
        }
      }

      if (activeStep === 3) {
        if (!Array.isArray(localData.keywords) || localData.keywords.length === 0) {
          errors.keywords = "At least one keyword is required";
        }
      }

      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    };

    // Check if current step is valid
    const isCurrentStepValid = () => {
      switch (activeStep) {
        case 1:
          return !!localData.name?.trim() && !!localData.url?.trim() && /^https?:\/\//i.test(localData.url);
        case 2:
          return !!localData.description?.trim() && localData.description.trim().length >= 50;
        case 3:
          return Array.isArray(localData.keywords) && localData.keywords.length > 0;
        default:
          return false;
      }
    };

    // Navigate between steps
    const goToNextStep = () => {
      if (validateForm()) {
        setAnimationDirection(1);
        setActiveStep((prev) => Math.min(prev + 1, totalSteps));
      }
    };

    const goToPrevStep = () => {
      setAnimationDirection(-1);
      setActiveStep((prev) => Math.max(prev - 1, 1));
    };

    // Get description quality indicator
    const getDescriptionQualityIndicator = () => {
      const length = localData.description?.trim().length || 0;

      if (length === 0) return null;

      if (length < 50) {
        return {
          icon: <AlertCircle size={14} className="text-red-500" />,
          label: "Too brief - add more details",
          color: "text-red-600",
        };
      } else if (length < 100) {
        return {
          icon: <Info size={14} className="text-yellow-500" />,
          label: "Good start - add more specific features",
          color: "text-yellow-600",
        };
      } else if (length < 200) {
        return {
          icon: <Check size={14} className="text-green-500" />,
          label: "Good description",
          color: "text-green-600",
        };
      } else {
        return {
          icon: <Sparkles size={14} className="text-emerald-500" />,
          label: "Excellent! Very detailed description",
          color: "text-emerald-600",
        };
      }
    };

    // Handle form submission
    const handleSubmit = () => {
      if (validateForm()) {
        // Prepare data for API
        const dataToSave = {
          name: localData.name,
          description: localData.description,
          url: localData.url,
          keywords: localData.keywords || [],
        };

        onSave(dataToSave);
      }
    };

    // Handler for modal close with unsaved changes warning
    const handleModalClose = () => {
      if (formTouched && !isSaving) {
        const confirmed = window.confirm("You have unsaved changes. Are you sure you want to close this form?");
        if (!confirmed) return;
      }
      onClose();
    };

    // Handle escape key to close modal
    useEffect(() => {
      const handleEscKey = (e) => {
        if (e.key === "Escape") {
          handleModalClose();
        }
      };

      if (isOpen) {
        document.addEventListener("keydown", handleEscKey);
      }

      return () => {
        document.removeEventListener("keydown", handleEscKey);
      };
    }, [isOpen, formTouched, isSaving]);

    // Step content components
    const renderStepOne = () => (
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Product Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="e.g., FeedbackbyAI"
            className={`w-full p-3 border ${
              validationErrors.name ? "border-red-300 bg-red-50" : "border-gray-300"
            } rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800 transition-all duration-200`}
            value={localData.name || ""}
            onChange={handleChange("name")}
            required
          />
          {validationErrors.name && (
            <p className="mt-1 text-red-600 text-sm flex items-center">
              <AlertCircle size={14} className="mr-1" />
              {validationErrors.name}
            </p>
          )}
          <p className="mt-1 text-gray-500 text-xs">Choose a clear, specific name that reflects your product's purpose.</p>
        </div>
        {/* 
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Product URL <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <Globe size={18} className="text-gray-400" />
            </div>
            <input
              type="url"
              placeholder="https://feedbackbyai.com"
              className={`w-full p-3 pl-10 border ${
                validationErrors.url ? "border-red-300 bg-red-50" : "border-gray-300"
              } rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800 transition-all duration-200`}
              value={localData.url || ""}
              onChange={handleChange("url")}
              required
            />
          </div>
          {validationErrors.url && (
            <p className="mt-1 text-red-600 text-sm flex items-center">
              <AlertCircle size={14} className="mr-1" />
              {validationErrors.url}
            </p>
          )}
          <p className="mt-1 text-gray-500 text-xs">Include the full URL where users can learn more about your product.</p>
        </div> */}

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Product URL <span className="text-red-500">*</span>
          </label>
          <div className="relative flex items-center">
            <div className="absolute left-3 flex items-center pointer-events-none">
              <Globe size={18} className="text-gray-400 mr-1" />
              <span className="text-gray-400 border-r border-gray-300 pr-2 mr-2">https://</span>
            </div>
            <input
              type="text"
              placeholder="example.com"
              className={`w-full p-3 pl-28 border ${
                validationErrors.url ? "border-red-300 bg-red-50" : "border-gray-300"
              } rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800 transition-all duration-200`}
              value={(localData.url || "").replace(/^https?:\/\//, "")}
              onChange={(e) => {
                const input = e.target.value;
                // Automatically add https:// prefix if not present
                const newUrl = input.startsWith("http://") || input.startsWith("https://") ? input : `https://${input}`;
                setLocalData({ ...localData, url: newUrl });
                setFormTouched(true);
                // Clear validation error when field is changed
                if (validationErrors.url) {
                  setValidationErrors({ ...validationErrors, url: null });
                }
              }}
              required
            />
          </div>
          {validationErrors.url && (
            <p className="mt-1 text-red-600 text-sm flex items-center">
              <AlertCircle size={14} className="mr-1" />
              {validationErrors.url}
            </p>
          )}
          <p className="mt-1 text-gray-500 text-xs">Just enter your domain name - we'll handle the "https://" part for you.</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-100 mt-4">
          <div className="flex items-start">
            <Lightbulb className="text-blue-500 mr-3 mt-0.5 flex-shrink-0" size={20} />
            <div>
              <h4 className="font-medium text-blue-800 text-sm">Product Details Matter</h4>
              <p className="text-blue-700 text-xs mt-1">
                Clear product information helps us find the most relevant leads for your business. Be specific to improve results.
              </p>
            </div>
          </div>
        </div>
      </div>
    );

    const renderStepTwo = () => (
      <div className="space-y-5">
        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="block text-sm font-semibold text-gray-700">
              Product Description <span className="text-red-500">*</span>
            </label>
            {getDescriptionQualityIndicator() && (
              <div className={`flex items-center text-xs ${getDescriptionQualityIndicator().color}`}>
                {getDescriptionQualityIndicator().icon}
                <span className="ml-1">{getDescriptionQualityIndicator().label}</span>
              </div>
            )}
          </div>
          <textarea
            placeholder="Describe your product in detail. What problems does it solve? Who is your target audience? What are its key features?"
            className={`w-full p-3 border ${
              validationErrors.description
                ? "border-red-300 bg-red-50"
                : descriptionFocused
                ? "border-green-300 ring-2 ring-green-100"
                : "border-gray-300"
            } rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800 transition-all duration-200`}
            rows="6"
            value={localData.description || ""}
            onChange={handleChange("description")}
            onFocus={() => setDescriptionFocused(true)}
            onBlur={() => setDescriptionFocused(false)}
            required
          />
          {validationErrors.description && (
            <p className="mt-1 text-red-600 text-sm flex items-center">
              <AlertCircle size={14} className="mr-1" />
              {validationErrors.description}
            </p>
          )}

          <div className="mt-2 flex justify-between items-center">
            <p className="text-gray-500 text-xs">Be specific about features and use cases to improve lead matching.</p>
            <span
              className={`text-xs ${
                (localData.description?.length || 0) < 50
                  ? "text-red-500"
                  : (localData.description?.length || 0) < 100
                  ? "text-yellow-500"
                  : "text-green-500"
              }`}
            >
              {localData.description?.length || 0} characters
            </span>
          </div>
        </div>

        <div>
          <button
            type="button"
            onClick={() => setShowAdvancedTips(!showAdvancedTips)}
            className="text-blue-600 text-sm flex items-center hover:text-blue-800 transition-colors"
          >
            <HelpCircle size={16} className="mr-1" />
            {showAdvancedTips ? "Hide tips" : "Show description tips"}
          </button>

          <AnimatePresence>
            {showAdvancedTips && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-3 bg-gray-50 rounded-lg border border-gray-200 p-4"
              >
                <h4 className="font-medium text-gray-800 text-sm mb-2">Tips for a Great Product Description</h4>
                <ul className="text-xs text-gray-600 space-y-2">
                  <li className="flex items-start">
                    <Check size={14} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>
                      Include your <strong>target audience</strong> (e.g., "designed for marketing teams")
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Check size={14} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>
                      Describe the <strong>key problems</strong> your product solves
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Check size={14} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>
                      List <strong>specific features</strong> and benefits
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Check size={14} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>
                      Use <strong>industry terminology</strong> your customers would search for
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Check size={14} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>
                      Mention <strong>integrations</strong> or compatibility with other tools
                    </span>
                  </li>
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    );

    const renderStepThree = () => (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Keywords (Max: {keywordLimit}) <span className="text-red-500">*</span>
          </label>
          <KeywordsInput
            value={localData.keywords || []}
            onChange={handleKeywordsChange}
            maxKeywords={keywordLimit}
            description={localData.description}
            isEdit={isEdit}
            handleUpgradeClick={handleUpgradeClick}
          />
          {validationErrors.keywords && (
            <p className="mt-1 text-red-600 text-sm flex items-center">
              <AlertCircle size={14} className="mr-1" />
              {validationErrors.keywords}
            </p>
          )}
        </div>

        <div className="mt-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100">
          <div className="flex items-center">
            <Sparkles className="text-emerald-500 mr-3 flex-shrink-0" size={24} />
            <div>
              <h4 className="font-medium text-emerald-800">Ready to Find Leads!</h4>
              <p className="text-emerald-700 text-sm mt-1">
                With your product details and keywords, we'll help you discover potential customers discussing similar needs.
              </p>
            </div>
          </div>
        </div>
      </div>
    );

    // Step navigation controls
    const renderStepControls = () => (
      <div className="flex justify-between items-center mt-8">
        <div className="flex items-center space-x-2">
          {Array.from({ length: totalSteps }).map((_, index) => (
            <div
              key={index}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                index + 1 === activeStep ? "bg-green-500 scale-125" : index + 1 < activeStep ? "bg-green-300" : "bg-gray-200"
              }`}
            />
          ))}
          <span className="text-xs text-gray-500 ml-2">
            Step {activeStep}/{totalSteps}: {getStepName(activeStep)}
          </span>
        </div>

        <div className="flex space-x-3">
          {activeStep > 1 && (
            <button
              onClick={goToPrevStep}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              type="button"
            >
              Back
            </button>
          )}

          {activeStep < totalSteps ? (
            <button
              onClick={goToNextStep}
              disabled={!isCurrentStepValid()}
              className={`px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center ${
                !isCurrentStepValid() ? "opacity-50 cursor-not-allowed" : ""
              }`}
              type="button"
            >
              <span>Continue</span>
              <ArrowRight size={16} className="ml-1" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!isCurrentStepValid() || isSaving}
              className={`px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center ${
                !isCurrentStepValid() || isSaving ? "opacity-50 cursor-not-allowed" : ""
              }`}
              type="button"
            >
              {isSaving && <Loader size={16} className="animate-spin mr-2" />}
              <span>{isEdit ? "Save Changes" : "Add Product"}</span>
            </button>
          )}
        </div>
      </div>
    );

    // Slide animation variants
    const slideVariants = {
      enter: (direction) => ({
        x: direction > 0 ? 500 : -500,
        opacity: 0,
      }),
      center: {
        x: 0,
        opacity: 1,
      },
      exit: (direction) => ({
        x: direction > 0 ? -500 : 500,
        opacity: 0,
      }),
    };

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl relative"
        >
          {/* Close button */}
          <button
            onClick={handleModalClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
            aria-label="Close"
          >
            <X size={20} className="text-gray-400" />
          </button>

          {/* Header */}
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-800">{isEdit ? "Edit Product" : "Add New Product"}</h3>
            <p className="text-gray-500 text-sm mt-1">
              {isEdit
                ? "Update your product details to find more relevant leads"
                : "Add your product details so we can find potential customers"}
            </p>
          </div>

          {/* Step content with animations */}
          <AnimatePresence mode="wait" custom={animationDirection}>
            <motion.div
              key={activeStep}
              custom={animationDirection}
              initial="enter"
              animate="center"
              exit="exit"
              variants={slideVariants}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              {activeStep === 1 && renderStepOne()}
              {activeStep === 2 && renderStepTwo()}
              {activeStep === 3 && renderStepThree()}
            </motion.div>
          </AnimatePresence>

          {/* Step controls */}
          {renderStepControls()}
        </motion.div>
      </div>
    );
  }
);

const GreenMarketingTab = ({ setActiveTab }) => {
  // ================ STATE MANAGEMENT ================
  // UI States
  const [showModal, setShowModal] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollToTop, setScrollToTop] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState("opportunities");
  const [viewMode, setViewMode] = useState("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");

  // Data States
  const [opportunities, setOpportunities] = useState([]);
  const [twitterLeads, setTwitterLeads] = useState([]);
  const [engagedLeads, setEngagedLeads] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [keywordLimit, setKeywordLimit] = useState(3); // Default to 3
  const [newPostsCount, setNewPostsCount] = useState(null);
  const [remainingCredits, setRemainingCredits] = useState(null);

  const [linkedInLeads, setLinkedInLeads] = useState([]);
  const [linkedInFilter, setLinkedInFilter] = useState("All");
  const [isLinkedInAnalysisInProgress, setIsLinkedInAnalysisInProgress] = useState(false);

  const [productDetails, setProductDetails] = useState({
    name: "",
    url: "",
    description: "",
    keywords: [],
  });
  const [modalData, setModalData] = useState({ name: "", url: "", description: "", keywords: [] });
  const [searchInputValue, setSearchInputValue] = useState("");

  // Filter States
  const [filter, setFilter] = useState("All");
  const [platformFilter, setPlatformFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("all");
  const [dateFilterStartTimestamp, setDateFilterStartTimestamp] = useState(null);
  const [twitterFilter, setTwitterFilter] = useState("All");
  const [engagementFilter, setEngagementFilter] = useState("All");
  const [verifiedFilter, setVerifiedFilter] = useState(false);
  const [engagedSearchQuery, setEngagedSearchQuery] = useState("");

  // API Statesg
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAnalysisInProgress, setIsAnalysisInProgress] = useState(false);
  const [isTwitterAnalysisInProgress, setIsTwitterAnalysisInProgress] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("error");
  const navigate = useNavigate();
  const renderCount = useRef(0);
  const lastFilteredResults = useRef({});

  // User States
  const [userDetails, setUserDetails] = useState(null);
  const [userPlan, setUserPlan] = useState("");
  const [isPremium, setIsPremium] = useState(false);
  const [credits, setCredits] = useState(0);

  // Define keyword limits based on plan
  const keywordLimits = {
    Free: 3,
    Starter: 3,
    Innovator: 3,
    Visionary: 5,
    Pioneer: 10,
  };

  // Inside GreenMarketingTab component body
  useEffect(() => {
    const now = Date.now();
    let startTimestamp = null; // null means 'all time'

    if (dateFilter === "24h") {
      startTimestamp = now - 24 * 60 * 60 * 1000;
    } else if (dateFilter === "7d") {
      startTimestamp = now - 7 * 24 * 60 * 60 * 1000;
    } else if (dateFilter === "30d") {
      startTimestamp = now - 30 * 24 * 60 * 60 * 1000;
    }
    // For 'all', startTimestamp remains null

    setDateFilterStartTimestamp(startTimestamp); // Update the state
  }, [dateFilter]); // Dependency array ensures this runs only when dateFilter changes

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInputValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInputValue]);

  const handleSaveProduct = (formData) => {
    // Log the exact data we're sending to help with debugging
    console.log("Sending to API:", formData);

    // Update the parent component state
    setProductDetails(formData);

    // Call appropriate function based on whether we're editing or creating
    if (selectedProduct) {
      // For edit, directly call the API
      setIsSaving(true);
      axios
        .put(`update-product/${selectedProduct.id}/`, {
          name: formData.name,
          description: formData.description,
          url: formData.url,
          keywords: formData.keywords,
        })
        .then((response) => {
          const updatedProduct = response.data;
          setProducts((prevProducts) => prevProducts.map((p) => (p.id === selectedProduct.id ? updatedProduct : p)));
          setSelectedProduct(updatedProduct);
          setShowModal(false);
          fetchUserPromotionPosts(updatedProduct.id);
          fetchTwitterLeads(updatedProduct.id);
          showAlertMessage("Product updated successfully!", "success");
          fetchUserProducts();
        })
        .catch((error) => {
          console.error("Error editing product:", error);
          showAlertMessage("Failed to edit product. Please try again.");
        })
        .finally(() => {
          setIsSaving(false);
        });
    } else {
      // For create, directly call the API
      setIsSaving(true);
      axios
        .post("create-product/", {
          name: formData.name,
          description: formData.description,
          url: formData.url,
          keywords: formData.keywords,
        })
        .then((response) => {
          console.log("Create API response:", response.data);
          const newProductId = response.data.product_id;
          // Fetch the new product to get full details
          return axios.get("loggedin-user-products/").then((productsResponse) => {
            const newProduct = productsResponse.data.find((p) => p.id === newProductId);
            setProducts((prevProducts) => [...prevProducts, newProduct]);
            setSelectedProduct(newProduct);
            setShowModal(false);
            fetchUserPromotionPosts(newProduct.id);
            fetchTwitterLeads(newProduct.id);
            showAlertMessage("Product created successfully!", "success");
            fetchUserProducts();
          });
        })
        .catch((error) => {
          console.error("Error creating product:", error);
          showAlertMessage("Failed to create product. Please try again.");
        })
        .finally(() => {
          setIsSaving(false);
        });
    }
  };
  const showUpgradeModalAlert = (message) => {
    setUpgradeMessage(message);
    setShowUpgradeModal(true);
  };

  const showAlertMessage = (message, type = "error") => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  };

  const handleUpgradeClick = () => {
    // setActiveTab("subscriptions");
    navigate("/credits/");
  };

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ================ API CALLS ================

  const fetchUserDetails = async () => {
    try {
      const response = await axios.get("get-user-credits/");
      setUserDetails(response.data);
      setCredits(response.data.lead_credits);
      setUserPlan(response.data.current_plan);

      // Explicitly check if plan is NOT Free - set isPremium accordingly
      const userIsPremium = response.data.plan_name !== "Free";
      console.log("User premium status:", userIsPremium, "Plan:", response.data.plan_name);
      setIsPremium(userIsPremium);

      setKeywordLimit(response.data.keyword_limit || 3);
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      // Default to non-premium if fetch fails for safety
      setIsPremium(false);
    }
  };

  // Fetch user products
  const fetchUserProducts = async () => {
    try {
      const response = await axios.get("loggedin-user-products/");
      const fetchedProducts = response.data;
      setProducts(fetchedProducts);
      if (fetchedProducts.length > 0) {
        const firstProduct = fetchedProducts[0];
        setSelectedProduct(firstProduct);
        if (firstProduct.id) {
          await fetchUserPromotionPosts(firstProduct.id);
          await fetchTwitterLeads(firstProduct.id);
          await fetchLinkedInLeads(firstProduct.id);
        }
      }
    } catch (error) {
      console.error("Error fetching user products:", error);
      showAlertMessage("Failed to fetch user products.");
    }
  };

  // Fetch user promotion posts (Reddit opportunities)
  const fetchUserPromotionPosts = async (productId) => {
    try {
      const response = await axios.get(`fetch-user-promotion-posts/${productId}/`, {
        params: { platform: "reddit" },
      });

      const redditOpportunities = response.data.filter((post) => !post.platform || post.platform === "reddit");

      const processedOpportunities = redditOpportunities.map((opportunity) => {
        if (opportunity.details && typeof opportunity.details.lead_quality_score === "string") {
          opportunity.details.lead_quality_score = parseInt(opportunity.details.lead_quality_score, 10) || 50;
        }
        return opportunity;
      });

      setOpportunities(processedOpportunities);

      if (processedOpportunities.length > 0) {
        setExpandedCard(processedOpportunities[0].id);
      }
    } catch (error) {
      console.error("Error fetching user promotion posts:", error);
      showAlertMessage("Failed to fetch leads. Please try again later.");
    }
  };

  // Fetch Twitter leads
  const fetchTwitterLeads = async (productId) => {
    try {
      const response = await axios.get(`fetch-user-promotion-posts/${productId}/`, {
        params: { platform: "twitter" },
      });

      const twitterPosts = response.data.filter((post) => post.platform === "twitter");

      setTwitterLeads(twitterPosts);

      return twitterPosts;
    } catch (error) {
      console.error("Error fetching Twitter leads:", error);
      return [];
    }
  };

  // Fetch engaged leads
  const fetchEngagedLeads = async () => {
    try {
      const response = await axios.get("leads-engaged/");
      setEngagedLeads(response.data);
    } catch (error) {
      console.error("Error fetching engaged leads:", error);
    }
  };

  // Create product
  const createProduct = async (e) => {
    if (e) e.preventDefault();

    // setIsLoading(true);
    setIsSaving(true);
    try {
      const response = await axios.post("create-product/", productDetails);
      const newProduct = response.data;
      setProducts((prevProducts) => [...prevProducts, newProduct]);
      setSelectedProduct(newProduct);
      setShowModal(false);

      await fetchUserPromotionPosts(newProduct.id);
      await fetchTwitterLeads(newProduct.id);

      setRefreshKey((prevKey) => prevKey + 1);
      showAlertMessage("Product created successfully!", "success");
    } catch (error) {
      console.error("Error creating product:", error);
      showAlertMessage("Failed to create product. Please try again.");
    } finally {
      // setIsLoading(false);
      setIsSaving(false);
    }
  };

  // Edit product
  const editProduct = async (e) => {
    if (e) e.preventDefault();

    // setIsLoading(true);
    setIsSaving(true);
    try {
      const response = await axios.put(`update-product/${selectedProduct.id}/`, productDetails);
      const updatedProduct = response.data;
      setProducts((prevProducts) => prevProducts.map((p) => (p.id === selectedProduct.id ? updatedProduct : p)));
      setSelectedProduct(updatedProduct);
      setShowModal(false);

      await fetchUserPromotionPosts(updatedProduct.id);
      await fetchTwitterLeads(updatedProduct.id);

      setRefreshKey((prevKey) => prevKey + 1);
      showAlertMessage("Product updated successfully!", "success");
    } catch (error) {
      console.error("Error editing product:", error);
      showAlertMessage("Failed to edit product. Please try again.");
    } finally {
      // setIsLoading(false);
      setIsSaving(false);
    }
  };

  // Fetch Reddit analysis
  // const fetchRedditAnalysis = async (e) => {
  //   if (e) e.preventDefault();

  //   if (isAnalysisInProgress) return;

  //   if (!selectedProduct || !selectedProduct.id || credits < 1) {
  //     showAlertMessage("Not enough credits. Please upgrade your plan.");
  //     return;
  //   }

  //   setIsAnalysisModalOpen(true);
  //   setIsRefreshing(true);
  //   setIsAnalysisInProgress(true);
  //   setAnalysisProgress(0);

  //   try {
  //     let isMounted = true;

  //     const progressInterval = setInterval(() => {
  //       if (isMounted) {
  //         setAnalysisProgress((prev) => Math.min(prev + 5, 95));
  //       }
  //     }, 2000);

  //     const response = await axios.post(`fetch-analysis/${selectedProduct.id}/`);
  //     // grab posts_count & remaining_credits
  //     setNewPostsCount(response.data.posts_count);
  //     setRemainingCredits(response.data.remaining_credits);

  //     clearInterval(progressInterval);

  //     if (isMounted) {
  //       setAnalysisProgress(100);

  //       await fetchUserPromotionPosts(selectedProduct.id);
  //       await fetchUserDetails();

  //       setActiveSubTab("opportunities");

  //       setTimeout(() => {
  //         setIsAnalysisModalOpen(false);
  //         setIsRefreshing(false);
  //         setIsAnalysisInProgress(false);
  //       }, 2000);
  //     }

  //     return () => {
  //       isMounted = false;
  //       clearInterval(progressInterval);
  //     };
  //   } catch (error) {
  //     console.error("Error fetching Reddit analysis:", error);
  //     setIsAnalysisModalOpen(false);
  //     setIsRefreshing(false);
  //     setIsAnalysisInProgress(false);
  //     showAlertMessage("Failed to fetch Reddit analysis. Please try again later.");

  //     // Check if this is a limits/upgrade error
  //     if (error.response && error.response.status === 402) {
  //       const errorMessage = error.response.data.message || "You've reached your plan limits. Please upgrade for more access.";

  //       // Show an upgrade alert
  //       showUpgradeModalAlert(errorMessage);
  //     } else {
  //       // Regular error handling
  //       showAlertMessage("Failed to fetch Reddit analysis. Please try again.");
  //     }
  //   }
  // };

  const fetchRedditAnalysis = async (e) => {
    if (e) e.preventDefault();

    if (isAnalysisInProgress) return;

    if (!selectedProduct || !selectedProduct.id || credits < 1) {
      showAlertMessage("Not enough credits. Please upgrade your plan.");
      return;
    }

    setIsAnalysisModalOpen(true);
    setIsRefreshing(true);
    setIsAnalysisInProgress(true);
    setAnalysisProgress(0);

    try {
      let isMounted = true;

      const progressInterval = setInterval(() => {
        if (isMounted) {
          setAnalysisProgress((prev) => Math.min(prev + 5, 95));
        }
      }, 2000);

      const response = await axios.post(`fetch-analysis/${selectedProduct.id}/`);
      // grab posts_count & remaining_credits
      setNewPostsCount(response.data.posts_count);
      setRemainingCredits(response.data.remaining_credits);

      clearInterval(progressInterval);

      if (isMounted) {
        setAnalysisProgress(100);

        await fetchUserPromotionPosts(selectedProduct.id);
        await fetchUserDetails();

        setActiveSubTab("opportunities");

        // Only update loading states, NOT closing the modal
        setIsRefreshing(false);
        setIsAnalysisInProgress(false);

        // REMOVED: Do not auto-close the modal
        // setTimeout(() => {
        //   setIsAnalysisModalOpen(false);
        //   setIsRefreshing(false);
        //   setIsAnalysisInProgress(false);
        // }, 2000);
      }

      return () => {
        isMounted = false;
        clearInterval(progressInterval);
      };
    } catch (error) {
      console.error("Error fetching Reddit analysis:", error);
      setIsAnalysisModalOpen(false);
      setIsRefreshing(false);
      setIsAnalysisInProgress(false);
      showAlertMessage("Failed to fetch Reddit analysis. Please try again later.");

      // Check if this is a limits/upgrade error
      if (error.response && error.response.status === 402) {
        const errorMessage = error.response.data.message || "You've reached your plan limits. Please upgrade for more access.";

        // Show an upgrade alert
        showUpgradeModalAlert(errorMessage);
      } else {
        // Regular error handling
        showAlertMessage("Failed to fetch Reddit analysis. Please try again.");
      }
    }
  };

  const fetchTwitterAnalysis = async (e) => {
    if (e) e.preventDefault();

    if (isTwitterAnalysisInProgress) return;

    setIsAnalysisModalOpen(true);
    setIsRefreshing(true);
    setIsTwitterAnalysisInProgress(true);
    setAnalysisProgress(0);

    try {
      let isMounted = true;

      const progressInterval = setInterval(() => {
        if (isMounted) {
          setAnalysisProgress((prev) => Math.min(prev + 5, 95));
        }
      }, 2000);

      const response = await axios.post(`fetch-twitter-analysis/${selectedProduct.id}/`);
      // grab posts_count & remaining_credits
      setNewPostsCount(response.data.posts_count);
      setRemainingCredits(response.data.remaining_credits);

      clearInterval(progressInterval);

      if (isMounted) {
        setAnalysisProgress(100);
        await fetchTwitterLeads(selectedProduct.id);
        await fetchUserDetails();

        setActiveSubTab("twitter");

        // Only update loading states, NOT closing the modal
        setIsRefreshing(false);
        setIsTwitterAnalysisInProgress(false);

        // REMOVED: Do not auto-close the modal
        // setTimeout(() => {
        //   setIsAnalysisModalOpen(false);
        //   setIsRefreshing(false);
        //   setIsTwitterAnalysisInProgress(false);
        // }, 2000);
      }

      return () => {
        isMounted = false;
        clearInterval(progressInterval);
      };
    } catch (error) {
      console.error("Error fetching Twitter analysis:", error);

      // Close the progress modal on error
      setIsAnalysisModalOpen(false);
      setIsRefreshing(false);
      setIsTwitterAnalysisInProgress(false);

      // Check if this is a limits/upgrade error
      if (error.response && error.response.status === 402) {
        const errorMessage = error.response.data.message || "You've reached your plan limits. Please upgrade for more access.";

        // Show an upgrade alert
        showUpgradeModalAlert(errorMessage);
      } else {
        // Regular error handling
        showAlertMessage("Failed to fetch Twitter analysis. Please try again.");
      }
    }
  };

  // Add the fetch function for LinkedIn
  const fetchLinkedInAnalysis = async (e) => {
    if (e) e.preventDefault();

    if (isLinkedInAnalysisInProgress) return;

    setIsAnalysisModalOpen(true);
    setIsRefreshing(true);
    setIsLinkedInAnalysisInProgress(true);
    setAnalysisProgress(0);

    try {
      let isMounted = true;

      const progressInterval = setInterval(() => {
        if (isMounted) {
          setAnalysisProgress((prev) => Math.min(prev + 5, 95));
        }
      }, 2000);

      const response = await axios.post(`fetch-linkedin-analysis/${selectedProduct.id}/`);
      setNewPostsCount(response.data.posts_count);
      setRemainingCredits(response.data.remaining_credits);

      clearInterval(progressInterval);

      if (isMounted) {
        setAnalysisProgress(100);
        await fetchLinkedInLeads(selectedProduct.id);
        await fetchUserDetails();

        setActiveSubTab("linkedin");

        // Only update loading states, NOT closing the modal
        setIsRefreshing(false);
        setIsLinkedInAnalysisInProgress(false);
      }

      return () => {
        isMounted = false;
        clearInterval(progressInterval);
      };
    } catch (error) {
      console.error("Error fetching LinkedIn analysis:", error);
      setIsAnalysisModalOpen(false);
      setIsRefreshing(false);
      setIsLinkedInAnalysisInProgress(false);

      // Check if this is a limits/upgrade error
      if (error.response && error.response.status === 402) {
        const errorMessage = error.response.data.message || "You've reached your plan limits. Please upgrade for more access.";
        showUpgradeModalAlert(errorMessage);
      } else {
        showAlertMessage("Failed to fetch LinkedIn analysis. Please try again.");
      }
    }
  };

  // Add the fetch LinkedIn leads function
  const fetchLinkedInLeads = async (productId) => {
    try {
      const response = await axios.get(`fetch-user-promotion-posts/${productId}/`, {
        params: { platform: "linkedin" }, // CORRECTED SPELLING
      });

      const linkedInPosts = response.data.filter((post) => post.platform === "linkedin");
      setLinkedInLeads(linkedInPosts);
      return linkedInPosts;
    } catch (error) {
      console.error("Error fetching LinkedIn leads:", error);
      return [];
    }
  };
  // const fetchTwitterAnalysis = async (e) => {
  //   if (e) e.preventDefault();

  //   if (isTwitterAnalysisInProgress) return;

  //   setIsAnalysisModalOpen(true);
  //   setIsRefreshing(true);
  //   setIsTwitterAnalysisInProgress(true);
  //   setAnalysisProgress(0);

  //   try {
  //     let isMounted = true;

  //     const progressInterval = setInterval(() => {
  //       if (isMounted) {
  //         setAnalysisProgress((prev) => Math.min(prev + 5, 95));
  //       }
  //     }, 2000);

  //     const response = await axios.post(`fetch-twitter-analysis/${selectedProduct.id}/`);
  //     // grab posts_count & remaining_credits
  //     setNewPostsCount(response.data.posts_count);
  //     setRemainingCredits(response.data.remaining_credits);

  //     clearInterval(progressInterval);

  //     if (isMounted) {
  //       setAnalysisProgress(100);
  //       await fetchTwitterLeads(selectedProduct.id);
  //       await fetchUserDetails();

  //       setActiveSubTab("twitter");

  //       setTimeout(() => {
  //         setIsAnalysisModalOpen(false);
  //         setIsRefreshing(false);
  //         setIsTwitterAnalysisInProgress(false);
  //       }, 2000);
  //     }

  //     return () => {
  //       isMounted = false;
  //       clearInterval(progressInterval);
  //     };
  //   } catch (error) {
  //     console.error("Error fetching Twitter analysis:", error);

  //     // Close the progress modal
  //     setIsAnalysisModalOpen(false);
  //     setIsRefreshing(false);
  //     setIsTwitterAnalysisInProgress(false);

  //     // Check if this is a limits/upgrade error
  //     if (error.response && error.response.status === 402) {
  //       const errorMessage = error.response.data.message || "You've reached your plan limits. Please upgrade for more access.";

  //       // Show an upgrade alert
  //       showUpgradeModalAlert(errorMessage);
  //     } else {
  //       // Regular error handling
  //       showAlertMessage("Failed to fetch Twitter analysis. Please try again.");
  //     }
  //   }
  // };

  // Toggle post completion
  const togglePostCompletion = async (postId) => {
    try {
      const response = await axios.post(`toggle-post-completion/${postId}/`);

      // Update both opportunities and Twitter leads
      setOpportunities((prevOpportunities) =>
        prevOpportunities
          .map((opp) => (opp.id === postId ? { ...opp, open_insight: response.data.open_insight } : opp))
          .sort((a, b) => (a.open_insight === b.open_insight ? 0 : a.open_insight ? 1 : -1))
      );

      setTwitterLeads((prevLeads) =>
        prevLeads
          .map((lead) => (lead.id === postId ? { ...lead, open_insight: response.data.open_insight } : lead))
          .sort((a, b) => (a.open_insight === b.open_insight ? 0 : a.open_insight ? 1 : -1))
      );

      setExpandedCard(null);
      fetchEngagedLeads();
    } catch (error) {
      console.error("Error toggling post completion:", error);
      showAlertMessage("Failed to update engagement status.");
    }
  };

  // ================ FILTERING FUNCTIONS ================
  // Filter Reddit opportunities
  // const filterOpportunities = useCallback(
  //   (opportunities) => {
  //     return opportunities.filter((opp) => {
  //       const platform = opp.details?.platform || (opp.original_post ? opp.original_post.platform : "");
  //       let timestamp = opp.details?.timestamp || opp.created_at;

  //       if (!timestamp && opp.original_post) {
  //         timestamp = opp.original_post.created_at;
  //       }

  //       const oppDate = new Date(timestamp);
  //       const now = new Date();

  //       const matchesIntent =
  //         filter === "All" ||
  //         (filter === "High Intent" && opp.details?.lead_quality_score >= 80) ||
  //         (filter === "Medium Intent" && opp.details?.lead_quality_score >= 60 && opp.details?.lead_quality_score < 80) ||
  //         (filter === "Low Intent" && opp.details?.lead_quality_score < 60);

  //       const matchesPlatform = platformFilter === "All" || platform.toLowerCase() === platformFilter.toLowerCase();

  //       const matchesDate =
  //         dateFilter === "all" ||
  //         (dateFilter === "24h" && now - oppDate <= 24 * 60 * 60 * 1000) ||
  //         (dateFilter === "7d" && now - oppDate <= 7 * 24 * 60 * 60 * 1000) ||
  //         (dateFilter === "30d" && now - oppDate <= 30 * 24 * 60 * 60 * 1000);

  //       // If search query is provided, filter by that as well
  //       const matchesSearch =
  //         !searchQuery ||
  //         (opp.details?.post_title && opp.details.post_title.toLowerCase().includes(searchQuery.toLowerCase())) ||
  //         (opp.details?.post_excerpt && opp.details.post_excerpt.toLowerCase().includes(searchQuery.toLowerCase()));

  //       return matchesIntent && matchesPlatform && matchesDate && matchesSearch;
  //     });
  //   },
  //   [filter, platformFilter, dateFilter, searchQuery]
  // );
  const filterOpportunities = useCallback(
    (opportunitiesToFilter) => {
      // Filter logic using the stable dateFilterStartTimestamp from state
      return opportunitiesToFilter.filter((opp) => {
        // --- Platform (Keep existing logic) ---
        const platform = opp.details?.platform || (opp.original_post ? opp.original_post.platform : "");
        const matchesPlatform = platformFilter === "All" || platform.toLowerCase() === platformFilter.toLowerCase();

        // --- Timestamp and Date Filter (MODIFIED) ---
        let timestamp = opp.details?.timestamp || opp.created_at;
        if (!timestamp && opp.original_post) {
          timestamp = opp.original_post.created_at;
        }
        const oppTimestamp = timestamp ? new Date(timestamp).getTime() : 0;

        // Use the stable dateFilterStartTimestamp from state
        // If dateFilterStartTimestamp is null (for 'all'), the condition short-circuits to true
        const matchesDate = !dateFilterStartTimestamp || oppTimestamp >= dateFilterStartTimestamp;

        // --- Intent (Keep existing logic) ---
        const score = opp.details?.lead_quality_score;
        const matchesIntent =
          filter === "All" ||
          (filter === "High Intent" && score >= 80) ||
          (filter === "Medium Intent" && score >= 60 && score < 80) ||
          (filter === "Low Intent" && score < 60);

        // --- Search (Keep existing logic) ---
        const lowerSearchQuery = searchQuery.toLowerCase();
        const matchesSearch =
          !searchQuery ||
          (opp.details?.post_title && opp.details.post_title.toLowerCase().includes(lowerSearchQuery)) ||
          (opp.details?.post_excerpt && opp.details.post_excerpt.toLowerCase().includes(lowerSearchQuery));

        // --- Combine ---
        return matchesIntent && matchesPlatform && matchesDate && matchesSearch;
      });
    },
    // Dependencies: Include the state variables the filter logic *reads*
    [filter, platformFilter, searchQuery, dateFilterStartTimestamp] // Use the timestamp state here
  );

  // Filter Twitter leads
  const filterTwitterLeads = useCallback(() => {
    return twitterLeads.filter((lead) => {
      const details = lead.details || {};

      // Intent filter
      const intentMatch =
        twitterFilter === "All" ||
        (twitterFilter === "High Intent" && details.lead_quality_score >= 80) ||
        (twitterFilter === "Medium Intent" && details.lead_quality_score >= 60 && details.lead_quality_score < 80) ||
        (twitterFilter === "Low Intent" && details.lead_quality_score < 60);

      // Engagement filter
      const engagementScore = (details.like_count || 0) + (details.retweet_count || 0) + (details.reply_count || 0);
      const engagementMatch =
        engagementFilter === "All" ||
        (engagementFilter === "High" && engagementScore >= 100) ||
        (engagementFilter === "Medium" && engagementScore >= 30 && engagementScore < 100) ||
        (engagementFilter === "Low" && engagementScore < 30);

      // Verified filter
      const verifiedMatch = !verifiedFilter || (details.author && details.author.verified);

      // Date filter
      const timestamp = details.timestamp || lead.created_at;
      const tweetDate = new Date(timestamp);
      const now = new Date();
      const dateMatch =
        dateFilter === "all" ||
        (dateFilter === "24h" && now - tweetDate <= 24 * 60 * 60 * 1000) ||
        (dateFilter === "7d" && now - tweetDate <= 7 * 24 * 60 * 60 * 1000) ||
        (dateFilter === "30d" && now - tweetDate <= 30 * 24 * 60 * 60 * 1000);

      // Search query filter
      const searchMatch =
        !searchQuery ||
        (details.post_title && details.post_title.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (details.post_excerpt && details.post_excerpt.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (details.user_handle && details.user_handle.toLowerCase().includes(searchQuery.toLowerCase()));

      return intentMatch && engagementMatch && verifiedMatch && dateMatch && searchMatch;
    });
  }, [twitterLeads, twitterFilter, engagementFilter, verifiedFilter, dateFilter, searchQuery]);

  // ================ FORM HANDLING ================

  const handleProductNameChange = useCallback((e) => {
    setProductDetails((prev) => ({ ...prev, name: e.target.value }));
  }, []);

  const handleProductUrlChange = useCallback((e) => {
    setProductDetails((prev) => ({ ...prev, url: e.target.value }));
  }, []);

  const handleProductDescriptionChange = useCallback((e) => {
    setProductDetails((prev) => ({ ...prev, description: e.target.value }));
  }, []);

  // Form validation
  const formIsValid = () => {
    const { name, url, description, keywords } = productDetails;
    return name.trim() !== "" && url.trim() !== "" && description.trim() !== "" && keywords.length > 0;
  };

  // Handle keyword change
  // const handleKeywordChange = (e) => {
  //   const keywords = e.target.value.split(",").map((keyword) => keyword.trim());

  //   if (keywords.length > (keywordLimits[userPlan] || 3)) {
  //     showAlertMessage(`You can only add up to ${keywordLimits[userPlan] || 3} keywords based on your ${userPlan} plan.`);
  //     return;
  //   }

  //   setProductDetails({ ...productDetails, keywords });
  // };

  const memoizedShowAlertMessage = useCallback((message, type = "error") => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  }, []);

  // Then update the handleKeywordChange to use this memoized function
  const handleKeywordChange = useCallback(
    (e) => {
      const keywords = e.target.value.split(",").map((keyword) => keyword.trim());

      if (keywords.length > (keywordLimits[userPlan] || 3)) {
        memoizedShowAlertMessage(`You can only add up to ${keywordLimits[userPlan] || 3} keywords based on your ${userPlan} plan.`);
        return;
      }

      setProductDetails((prev) => ({ ...prev, keywords }));
    },
    [keywordLimits, userPlan, memoizedShowAlertMessage]
  );

  // Toggle card expansion
  const toggleCard = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  // Open edit modal
  const openEditModal = (product) => {
    setProductDetails({
      name: product.name,
      url: product.product_url,
      description: product.description,
      keywords: Array.isArray(product.keywords)
        ? product.keywords
        : product.keywords
        ? product.keywords.split(",").map((k) => k.trim())
        : [],
    });
    setShowModal(true);
  };

  // ================ LIFECYCLE HOOKS ================
  // Fetch user details on component mount
  useEffect(() => {
    fetchUserDetails();
  }, []);

  // Fetch user products when user details change
  useEffect(() => {
    if (userDetails) {
      fetchUserProducts();
    }
  }, [userDetails]);

  // Fetch engaged leads on component mount
  useEffect(() => {
    fetchEngagedLeads();
  }, []);

  // Add scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 500) {
        setScrollToTop(true);
      } else {
        setScrollToTop(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ================ COMPONENT RENDERS ================
  // Alert banner component
  // Update your AlertBanner component
  const AlertBanner = ({ message, onClose, type = "error" }) => {
    // Special handling for upgrade-related alerts
    if (type === "upgrade") {
      return (
        <div className="bg-purple-100 border-purple-400 text-purple-800 px-4 py-4 rounded-lg border mb-4 relative" role="alert">
          <div className="flex items-center mb-2">
            <Zap size={20} className="mr-2 text-purple-600" />
            <span className="font-medium text-lg">{message}</span>
            <button className="absolute top-0 right-0 p-2" onClick={onClose}>
              <X size={18} />
            </button>
          </div>
          <div className="mt-2 flex justify-end">
            <button
              onClick={handleUpgradeClick}
              className="bg-purple-600 text-white px-4 py-2 rounded font-medium hover:bg-purple-700 transition-colors duration-200 flex items-center"
            >
              <Award className="mr-2 w-4 h-4" />
              Upgrade Now
            </button>
          </div>
        </div>
      );
    }

    // Original alert styles for other types
    const bgColor =
      type === "error"
        ? "bg-red-100 border-red-400 text-red-700"
        : type === "success"
        ? "bg-green-100 border-green-400 text-green-700"
        : "bg-blue-100 border-blue-400 text-blue-700";

    const icon =
      type === "error" ? (
        <AlertCircle size={18} className="mr-2" />
      ) : type === "success" ? (
        <CheckCircle size={18} className="mr-2" />
      ) : (
        <Info size={18} className="mr-2" />
      );

    return (
      <div className={`${bgColor} px-4 py-3 rounded-lg border mb-4 relative flex items-center`} role="alert">
        {icon}
        <span className="block sm:inline">{message}</span>
        <button className="absolute top-0 bottom-0 right-0 px-4 py-3" onClick={onClose}>
          <X size={18} />
        </button>
      </div>
    );
  };

  // Empty state component
  // const EmptyState = () => (
  //   <div className="text-center p-8 bg-white rounded-xl shadow-lg mb-12 max-w-2xl mx-auto border border-green-100">
  //     <div className="bg-green-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
  //       <Lightbulb className="w-8 h-8 text-green-500" />
  //     </div>
  //     <h2 className="text-2xl font-bold text-gray-800 mb-3">Welcome to Real-Time Buyer Intent Search!</h2>
  //     <p className="text-gray-600 mb-6">Let's get started by adding your first product to find qualified leads.</p>
  //     <button
  //       onClick={() => setShowModal(true)}
  //       className="bg-green-500 animate-pulse text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-all duration-200 shadow-md flex items-center justify-center space-x-2 mx-auto"
  //     >
  //       <Plus size={20} />
  //       <span>Add Your First Product</span>
  //     </button>
  //   </div>
  // );

  const EmptyState = () => (
    <div className="text-center p-10 bg-white rounded-2xl shadow-xl mb-16 max-w-3xl mx-auto border border-green-200">
      <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
        <Lightbulb className="w-10 h-10 text-green-600 animate-bounce" />
      </div>
      <h2 className="text-3xl font-extrabold text-gray-900 mb-4">Welcome to Real-Time Buyer Intent Search!</h2>
      <p className="text-gray-600 text-lg mb-8 max-w-xl mx-auto">
        Let’s kick things off by adding your first product and uncovering leads already searching for what you offer.
      </p>
      <button
        onClick={() => setShowModal(true)}
        className="bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-lg flex items-center justify-center space-x-3 mx-auto group"
      >
        <Plus size={22} className="group-hover:scale-110 transition-transform duration-200" />
        <span>Add Your First Product</span>
      </button>
    </div>
  );

  // Filter button component
  const FilterButton = ({ label, onClick, isActive, icon }) => (
    <button
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 flex items-center space-x-1 ${
        isActive
          ? activeSubTab === "twitter"
            ? "bg-blue-100 text-blue-800 border border-blue-200"
            : "bg-green-100 text-green-800 border border-green-200"
          : "bg-white hover:bg-gray-50 text-gray-700 border border-gray-200"
      }`}
    >
      {icon && icon}
      <span>{label}</span>
    </button>
  );

  // Navigation bar component
  // const NavigationBar = () => (
  //   <div className="bg-white shadow-sm rounded-xl mb-6 p-4">
  //     <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
  //       <div className="flex items-center space-x-4 w-full md:w-auto">
  //         <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-2 rounded-lg shadow-sm">
  //           <Sparkles className="w-6 h-6 text-white" />
  //         </div>
  //         <div>
  //           <h1 className="text-xl font-bold text-gray-800">Real-Time Buyer Intent Search</h1>
  //           <p className="text-sm text-gray-500">Find and engage with high-quality leads</p>
  //         </div>
  //       </div>

  //       <div className="flex space-x-3 w-full md:w-auto">
  //         {/* Enhanced Credits Display - Replace existing credits display */}
  //         {credits === 0 ? (
  //           <div className="flex items-center bg-gradient-to-r from-red-50 to-orange-50 px-4 py-3 rounded-lg border border-red-200 shadow-sm animate-pulse">
  //             <div className="flex-shrink-0 mr-3">
  //               <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
  //                 <Zap className="w-5 h-5 text-red-500" />
  //               </div>
  //             </div>
  //             <div className="flex-1">
  //               <p className="font-medium text-red-800">No Credits Left!</p>
  //               <p className="text-xs text-red-600">Upgrade to continue finding leads</p>
  //             </div>
  //             <button
  //               onClick={handleUpgradeClick}
  //               className="ml-3 px-4 py-2 bg-gradient-to-r from-red-600 to-orange-500 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105 flex items-center"
  //             >
  //               <Zap className="w-4 h-4 mr-1.5" />
  //               Upgrade Now
  //             </button>
  //           </div>
  //         ) : credits < 5 ? (
  //           <div className="flex items-center bg-gradient-to-r from-amber-50 to-yellow-50 px-4 py-2.5 rounded-lg border border-amber-200">
  //             <div className="flex-shrink-0 mr-3">
  //               <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
  //                 <Zap className="w-5 h-5 text-amber-500" />
  //               </div>
  //             </div>
  //             <div className="flex-1">
  //               <p className="font-medium text-amber-800">
  //                 <span className="text-lg">{credits}</span> {credits === 1 ? "Credit" : "Credits"} Left
  //               </p>
  //               <p className="text-xs text-amber-700 flex items-center">
  //                 <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />
  //                 Running low on credits!
  //               </p>
  //             </div>
  //             <button
  //               onClick={handleUpgradeClick}
  //               className="ml-3 px-3 py-1.5 bg-amber-100 text-amber-800 text-sm font-medium rounded-lg hover:bg-amber-200 transition-colors flex items-center"
  //             >
  //               <Plus className="w-4 h-4 mr-1" />
  //               Get More
  //             </button>
  //           </div>
  //         ) : (
  //           <div className="flex items-center bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-2.5 rounded-lg border border-green-200">
  //             <div className="flex-shrink-0 mr-3">
  //               <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
  //                 <Zap className="w-4 h-4 text-green-500" />
  //               </div>
  //             </div>
  //             <div>
  //               <p className="font-medium text-green-800">
  //                 <span className="text-lg">{credits}</span> {credits === 1 ? "Credit" : "Credits"} Remaining
  //               </p>
  //             </div>
  //           </div>
  //         )}

  //         <div className="flex-1 md:flex-none">
  //           <button
  //             onClick={() => setIsMenuOpen(!isMenuOpen)}
  //             className="md:hidden w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-50 rounded-lg text-green-800"
  //           >
  //             <Menu size={18} />
  //             <span>Menu</span>
  //           </button>

  //           <div className="hidden md:flex space-x-2">
  //             {activeSubTab === "opportunities" && (
  //               <button
  //                 onClick={fetchRedditAnalysis}
  //                 disabled={isRefreshing || isAnalysisInProgress || credits < 1}
  //                 className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
  //                 type="button"
  //               >
  //                 {isRefreshing || isAnalysisInProgress ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
  //                 <span>{isRefreshing || isAnalysisInProgress ? "Analyzing..." : "Find Reddit Leads"}</span>
  //               </button>
  //             )}

  //             {activeSubTab === "twitter" && (
  //               <button
  //                 onClick={fetchTwitterAnalysis}
  //                 disabled={isRefreshing || isTwitterAnalysisInProgress || credits < 1}
  //                 className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
  //                 type="button"
  //               >
  //                 {isRefreshing || isTwitterAnalysisInProgress ? (
  //                   <RefreshCw className="w-4 h-4 animate-spin" />
  //                 ) : (
  //                   <Twitter className="w-4 h-4" />
  //                 )}
  //                 <span>{isRefreshing || isTwitterAnalysisInProgress ? "Analyzing..." : "Find Twitter Leads"}</span>
  //               </button>
  //             )}

  //             {selectedProduct && (
  //               <button
  //                 onClick={() => openEditModal(selectedProduct)}
  //                 className="flex items-center space-x-2 px-4 py-2 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg transition-colors duration-200"
  //               >
  //                 <Edit className="w-4 h-4" />
  //                 <span>Edit Product</span>
  //               </button>
  //             )}
  //           </div>
  //         </div>
  //       </div>
  //     </div>

  //     {/* <AnimatePresence>
  //       {isMenuOpen && (
  //         <motion.div
  //           initial={{ opacity: 0, height: 0 }}
  //           animate={{ opacity: 1, height: "auto" }}
  //           exit={{ opacity: 0, height: 0 }}
  //           transition={{ duration: 0.2 }}
  //           className="md:hidden mt-4 space-y-2"
  //         >
  //           <div className="flex items-center space-x-2 px-4 py-3 bg-green-50 rounded-lg">
  //             <Zap className="w-5 h-5 text-green-500" />
  //             <span className="text-green-800 font-medium">{credits} Credits Available</span>
  //           </div>

  //           {activeSubTab === "opportunities" && (
  //             <button
  //               onClick={fetchRedditAnalysis}
  //               disabled={isRefreshing || isAnalysisInProgress || credits < 1}
  //               className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
  //             >
  //               {isRefreshing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
  //               <span>{isRefreshing ? "Analyzing..." : "Find Reddit Leads"}</span>
  //             </button>
  //           )}

  //           {activeSubTab === "twitter" && (
  //             <button
  //               onClick={fetchTwitterAnalysis}
  //               disabled={isRefreshing || isTwitterAnalysisInProgress || credits < 1}
  //               className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
  //             >
  //               {isRefreshing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Twitter className="w-4 h-4" />}
  //               <span>{isRefreshing ? "Analyzing..." : "Find Twitter Leads"}</span>
  //             </button>
  //           )}

  //           {activeSubTab === "linkedin" && (
  //             <button
  //               onClick={fetchLinkedInAnalysis}
  //               disabled={isRefreshing || isLinkedInAnalysisInProgress || credits < 1}
  //               className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
  //               type="button"
  //             >
  //               {isRefreshing || isLinkedInAnalysisInProgress ? (
  //                 <RefreshCw className="w-4 h-4 animate-spin" />
  //               ) : (
  //                 <Linkedin className="w-4 h-4" />
  //               )}
  //               <span>{isRefreshing || isLinkedInAnalysisInProgress ? "Analyzing..." : "Find LinkedIn Leads"}</span>
  //             </button>
  //           )}

  //           {selectedProduct && (
  //             <button
  //               onClick={() => openEditModal(selectedProduct)}
  //               className="w-full flex items-center justify-center space-x-2 px-4 py-3 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg transition-colors duration-200"
  //             >
  //               <Edit className="w-4 h-4" />
  //               <span>Edit Product</span>
  //             </button>
  //           )}
  //         </motion.div>
  //       )}
  //     </AnimatePresence> */}

  //     <AnimatePresence>
  //       {isMenuOpen && (
  //         <motion.div
  //           initial={{ opacity: 0, height: 0 }}
  //           animate={{ opacity: 1, height: "auto" }}
  //           exit={{ opacity: 0, height: 0 }}
  //           transition={{ duration: 0.2 }}
  //           className="md:hidden mt-4 space-y-2"
  //         >
  //           <div className="flex items-center space-x-2 px-4 py-3 bg-green-50 rounded-lg">
  //             <Zap className="w-5 h-5 text-green-500" />
  //             <span className="text-green-800 font-medium">{credits} Credits Available</span>
  //           </div>

  //           {activeSubTab === "opportunities" && (
  //             <button
  //               onClick={fetchRedditAnalysis}
  //               disabled={isRefreshing || isAnalysisInProgress || credits < 1}
  //               className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
  //             >
  //               {isRefreshing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
  //               <span>{isRefreshing ? "Analyzing..." : "Find Reddit Leads"}</span>
  //             </button>
  //           )}

  //           {activeSubTab === "twitter" && (
  //             <button
  //               onClick={fetchTwitterAnalysis}
  //               disabled={isRefreshing || isTwitterAnalysisInProgress || credits < 1}
  //               className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
  //             >
  //               {isRefreshing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Twitter className="w-4 h-4" />}
  //               <span>{isRefreshing ? "Analyzing..." : "Find Twitter Leads"}</span>
  //             </button>
  //           )}

  //           {activeSubTab === "linkedin" && (
  //             <button
  //               onClick={fetchLinkedInAnalysis}
  //               disabled={isRefreshing || isLinkedInAnalysisInProgress || credits < 1}
  //               className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
  //             >
  //               {isRefreshing || isLinkedInAnalysisInProgress ? (
  //                 <RefreshCw className="w-4 h-4 animate-spin" />
  //               ) : (
  //                 <Linkedin className="w-4 h-4" />
  //               )}
  //               <span>{isRefreshing || isLinkedInAnalysisInProgress ? "Analyzing..." : "Find LinkedIn Leads"}</span>
  //             </button>
  //           )}

  //           {selectedProduct && (
  //             <button
  //               onClick={() => openEditModal(selectedProduct)}
  //               className="w-full flex items-center justify-center space-x-2 px-4 py-3 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg transition-colors duration-200"
  //             >
  //               <Edit className="w-4 h-4" />
  //               <span>Edit Product</span>
  //             </button>
  //           )}
  //         </motion.div>
  //       )}
  //     </AnimatePresence>

  //     <div className="flex mt-6 border-b border-gray-200">
  //       <button
  //         onClick={() => setActiveSubTab("opportunities")}
  //         className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors duration-200 ${
  //           activeSubTab === "opportunities" ? "border-green-500 text-green-700" : "border-transparent text-gray-600 hover:text-gray-800"
  //         }`}
  //       >
  //         <div className="flex items-center space-x-2">
  //           <Globe size={16} />
  //           <span>Reddit</span>
  //         </div>
  //       </button>

  //       {/* <button
  //         onClick={() => setActiveSubTab("twitter")}
  //         className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors duration-200 ${
  //           activeSubTab === "twitter" ? "border-blue-500 text-blue-700" : "border-transparent text-gray-600 hover:text-gray-800"
  //         }`}
  //       >
  //         <div className="flex items-center space-x-2">
  //           <Twitter size={16} />
  //           <span>Twitter</span>
  //         </div>
  //       </button> */}
  //       <button
  //         onClick={() => setActiveSubTab("twitter")}
  //         className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors duration-200 ${
  //           activeSubTab === "twitter" ? "border-blue-500 text-blue-700" : "border-transparent text-gray-600 hover:text-gray-800"
  //         }`}
  //       >
  //         <div className="flex items-center space-x-2">
  //           <Twitter size={16} />
  //           <span>Twitter</span>
  //           {/* Add this PRO badge */}
  //           <span className="ml-1 bg-purple-200 text-purple-800 text-xs px-1.5 py-0.5 rounded-full flex items-center shadow-sm">
  //             <Lock size={8} className="mr-0.5" />
  //             PRO
  //           </span>
  //         </div>
  //       </button>

  //       <button
  //         onClick={() => setActiveSubTab("linkedin")}
  //         className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors duration-200 ${
  //           activeSubTab === "linkedin" ? "border-purple-500 text-purple-700" : "border-transparent text-gray-600 hover:text-gray-800"
  //         }`}
  //       >
  //         <div className="flex items-center space-x-2">
  //           <Linkedin size={16} />
  //           <span>LinkedIn</span>
  //           <span className="ml-1 bg-purple-200 text-purple-800 text-xs px-1.5 py-0.5 rounded-full flex items-center shadow-sm">
  //             <Lock size={8} className="mr-0.5" />
  //             PRO
  //           </span>
  //         </div>
  //       </button>

  //       <button
  //         onClick={() => setActiveSubTab("engaged")}
  //         className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors duration-200 ${
  //           activeSubTab === "engaged" ? "border-green-500 text-green-700" : "border-transparent text-gray-600 hover:text-gray-800"
  //         }`}
  //       >
  //         <div className="flex items-center space-x-2">
  //           <UserCheck size={16} />
  //           <span>Engaged Leads</span>
  //           {engagedLeads.length > 0 && (
  //             <span className="bg-green-100 text-green-800 text-xs px-1.5 py-0.5 rounded-full">{engagedLeads.length}</span>
  //           )}
  //         </div>
  //       </button>
  //     </div>
  //   </div>
  // );

  const NavigationBar = () => (
    <div className="bg-white shadow-sm rounded-xl mb-6 p-4">
      <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
        <div className="flex items-center space-x-4 w-full md:w-auto">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-2 rounded-lg shadow-sm">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Real-Time Buyer Intent Search</h1>
            <p className="text-sm text-gray-500">Find and engage with high-quality leads</p>
          </div>
        </div>

        <div className="flex space-x-3 w-full md:w-auto">
          {/* Enhanced Credits Display */}
          {credits === 0 ? (
            <div className="flex items-center bg-gradient-to-r from-red-50 to-orange-50 px-4 py-3 rounded-lg border border-red-200 shadow-sm animate-pulse">
              <div className="flex-shrink-0 mr-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-red-500" />
                </div>
              </div>
              <div className="flex-1">
                <p className="font-medium text-red-800">No Credits Left!</p>
                <p className="text-xs text-red-600">Upgrade to continue finding leads</p>
              </div>
              <button
                onClick={handleUpgradeClick}
                className="ml-3 px-4 py-2 bg-gradient-to-r from-red-600 to-orange-500 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105 flex items-center"
              >
                <Zap className="w-4 h-4 mr-1.5" />
                Upgrade Now
              </button>
            </div>
          ) : credits < 5 ? (
            <div className="flex items-center bg-gradient-to-r from-amber-50 to-yellow-50 px-4 py-2.5 rounded-lg border border-amber-200">
              <div className="flex-shrink-0 mr-3">
                <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-amber-500" />
                </div>
              </div>
              <div className="flex-1">
                <p className="font-medium text-amber-800">
                  <span className="text-lg">{credits}</span> {credits === 1 ? "Credit" : "Credits"} Left
                </p>
                <p className="text-xs text-amber-700 flex items-center">
                  <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                  Running low on credits!
                </p>
              </div>
              <button
                onClick={handleUpgradeClick}
                className="ml-3 px-3 py-1.5 bg-amber-100 text-amber-800 text-sm font-medium rounded-lg hover:bg-amber-200 transition-colors flex items-center"
              >
                <Plus className="w-4 h-4 mr-1" />
                Get More
              </button>
            </div>
          ) : (
            <div className="flex items-center bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-2.5 rounded-lg border border-green-200">
              <div className="flex-shrink-0 mr-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <Zap className="w-4 h-4 text-green-500" />
                </div>
              </div>
              <div>
                <p className="font-medium text-green-800">
                  <span className="text-lg">{credits}</span> {credits === 1 ? "Credit" : "Credits"} Remaining
                </p>
              </div>
            </div>
          )}

          <div className="flex-1 md:flex-none">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-50 rounded-lg text-green-800"
            >
              <Menu size={18} />
              <span>Menu</span>
            </button>

            <div className="hidden md:flex space-x-2">
              {activeSubTab === "opportunities" && (
                <button
                  onClick={fetchRedditAnalysis}
                  disabled={isRefreshing || isAnalysisInProgress || credits < 1}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  type="button"
                >
                  {isRefreshing || isAnalysisInProgress ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
                  <span>{isRefreshing || isAnalysisInProgress ? "Analyzing..." : "Find Reddit Leads"}</span>
                </button>
              )}

              {activeSubTab === "twitter" && (
                <button
                  onClick={fetchTwitterAnalysis}
                  disabled={isRefreshing || isTwitterAnalysisInProgress || credits < 1}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  type="button"
                >
                  {isRefreshing || isTwitterAnalysisInProgress ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Twitter className="w-4 h-4" />
                  )}
                  <span>{isRefreshing || isTwitterAnalysisInProgress ? "Analyzing..." : "Find Twitter Leads"}</span>
                </button>
              )}

              {activeSubTab === "linkedin" && (
                <button
                  onClick={fetchLinkedInAnalysis}
                  disabled={isRefreshing || isLinkedInAnalysisInProgress || credits < 1}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  type="button"
                >
                  {isRefreshing || isLinkedInAnalysisInProgress ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#ffffff">
                      <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z" />
                    </svg>
                  )}
                  <span>{isRefreshing || isLinkedInAnalysisInProgress ? "Analyzing..." : "Find LinkedIn Leads"}</span>
                </button>
              )}

              {selectedProduct && (
                <button
                  onClick={() => openEditModal(selectedProduct)}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg transition-colors duration-200"
                >
                  <Edit className="w-4 h-4" />
                  <span>Edit Product</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden mt-4 space-y-2"
          >
            <div className="flex items-center space-x-2 px-4 py-3 bg-green-50 rounded-lg">
              <Zap className="w-5 h-5 text-green-500" />
              <span className="text-green-800 font-medium">{credits} Credits Available</span>
            </div>

            {activeSubTab === "opportunities" && (
              <button
                onClick={fetchRedditAnalysis}
                disabled={isRefreshing || isAnalysisInProgress || credits < 1}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRefreshing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
                <span>{isRefreshing ? "Analyzing..." : "Find Reddit Leads"}</span>
              </button>
            )}

            {activeSubTab === "twitter" && (
              <button
                onClick={fetchTwitterAnalysis}
                disabled={isRefreshing || isTwitterAnalysisInProgress || credits < 1}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRefreshing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Twitter className="w-4 h-4" />}
                <span>{isRefreshing ? "Analyzing..." : "Find Twitter Leads"}</span>
              </button>
            )}

            {activeSubTab === "linkedin" && (
              <button
                onClick={fetchLinkedInAnalysis}
                disabled={isRefreshing || isLinkedInAnalysisInProgress || credits < 1}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRefreshing || isLinkedInAnalysisInProgress ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z" />
                  </svg>
                )}
                <span>{isRefreshing || isLinkedInAnalysisInProgress ? "Analyzing..." : "Find LinkedIn Leads"}</span>
              </button>
            )}

            {selectedProduct && (
              <button
                onClick={() => openEditModal(selectedProduct)}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg transition-colors duration-200"
              >
                <Edit className="w-4 h-4" />
                <span>Edit Product</span>
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-wrap mt-6 border-b border-gray-200">
        <button
          onClick={() => setActiveSubTab("opportunities")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors duration-200 ${
            activeSubTab === "opportunities" ? "border-green-500 text-green-700" : "border-transparent text-gray-600 hover:text-gray-800"
          }`}
        >
          <div className="flex items-center space-x-2">
            <Globe size={16} />
            <span>Reddit</span>
          </div>
        </button>

        <button
          onClick={() => setActiveSubTab("twitter")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors duration-200 ${
            activeSubTab === "twitter" ? "border-blue-500 text-blue-700" : "border-transparent text-gray-600 hover:text-gray-800"
          }`}
        >
          <div className="flex items-center space-x-2">
            <Twitter size={16} />
            <span>Twitter</span>
            <span className="ml-1 bg-purple-200 text-purple-800 text-xs px-1.5 py-0.5 rounded-full flex items-center shadow-sm">
              <Lock size={8} className="mr-0.5" />
              PRO
            </span>
          </div>
        </button>

        <button
          onClick={() => setActiveSubTab("linkedin")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors duration-200 ${
            activeSubTab === "linkedin" ? "border-purple-500 text-purple-700" : "border-transparent text-gray-600 hover:text-gray-800"
          }`}
        >
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill={activeSubTab === "linkedin" ? "#7e3af2" : "#71717a"}>
              <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z" />
            </svg>
            <span>LinkedIn</span>
            <span className="ml-1 bg-purple-200 text-purple-800 text-xs px-1.5 py-0.5 rounded-full flex items-center shadow-sm">
              <Lock size={8} className="mr-0.5" />
              PRO
            </span>
          </div>
        </button>

        <button
          onClick={() => setActiveSubTab("engaged")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors duration-200 ${
            activeSubTab === "engaged" ? "border-green-500 text-green-700" : "border-transparent text-gray-600 hover:text-gray-800"
          }`}
        >
          <div className="flex items-center space-x-2">
            <UserCheck size={16} />
            <span>Engaged Leads</span>
            {engagedLeads.length > 0 && (
              <span className="bg-green-100 text-green-800 text-xs px-1.5 py-0.5 rounded-full">{engagedLeads.length}</span>
            )}
          </div>
        </button>
      </div>
    </div>
  );

  // Product list component
  const ProductList = () => (
    <div className="bg-white shadow-sm rounded-lg p-4 mb-6">
      <h2 className="text-lg font-medium text-gray-800 mb-4">Your Products</h2>
      <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
        {products.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            isActive={selectedProduct && selectedProduct.id === product.id}
            onClick={(product) => {
              setSelectedProduct(product);
              fetchUserPromotionPosts(product.id);
              fetchTwitterLeads(product.id);
            }}
          />
        ))}
      </div>
      <button
        onClick={() => setShowModal(true)}
        className="w-full mt-4 flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
      >
        {/* <Plus size={18} /> */}
        <span>Edit Product</span>
      </button>
    </div>
  );

  // Product card component
  const ProductCard = ({ product, isActive, onClick }) => (
    <div
      onClick={() => onClick(product)}
      className={`cursor-pointer p-4 rounded-lg transition-all duration-200 ${
        isActive ? "bg-green-50 border-green-300 border" : "hover:bg-gray-50 border border-transparent"
      }`}
    >
      <div className="flex items-center space-x-3">
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            isActive ? "bg-green-500 text-white" : "bg-gray-200 text-gray-500"
          }`}
        >
          <Layout size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`text-sm font-medium truncate ${isActive ? "text-green-800" : "text-gray-700"}`}>{product.name}</h3>
          <p className="text-xs text-gray-500 truncate">
            {Array.isArray(product.keywords)
              ? product.keywords.slice(0, 3).join(", ")
              : typeof product.keywords === "string"
              ? product.keywords.split(",").slice(0, 3).join(", ")
              : ""}
          </p>
        </div>
      </div>
    </div>
  );

  // Progress bar component
  const ProgressBar = ({ value, className }) => (
    <div className={`w-full bg-gray-200 rounded-full h-2 ${className}`}>
      <div className="bg-green-500 h-2 rounded-full transition-all duration-300 ease-out" style={{ width: `${value}%` }} />
    </div>
  );

  // Action button component
  const ActionButton = ({ icon: Icon, label, onClick, primary = false, engaged = false, color = "green" }) => (
    <button
      onClick={onClick}
      className={`
        flex items-center justify-center space-x-2 px-4 py-2 rounded-lg
        text-sm font-medium transition-all duration-200 shadow-sm
        ${
          primary
            ? color === "blue"
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-green-600 text-white hover:bg-green-700"
            : engaged
            ? "bg-gray-200 text-gray-700 hover:bg-gray-300"
            : color === "blue"
            ? "bg-white text-blue-600 border border-blue-200 hover:bg-blue-50"
            : "bg-white text-green-600 border border-green-200 hover:bg-green-50"
        }
      `}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </button>
  );

  const handleDateFilterChange = useCallback((newDateFilter) => {
    setDateFilter(newDateFilter);
  }, []);

  const filterEngagedLeads = useCallback(() => {
    return engagedLeads.filter(
      (lead) =>
        !engagedSearchQuery ||
        lead.details?.post_title?.toLowerCase().includes(engagedSearchQuery.toLowerCase()) ||
        lead.details?.post_excerpt?.toLowerCase().includes(engagedSearchQuery.toLowerCase())
    );
  }, [engagedLeads, engagedSearchQuery]);

  const RedditFilterSection = () => (
    <div className="bg-white shadow-sm rounded-lg p-4 mb-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 space-y-3 sm:space-y-0">
        <h2 className="text-lg font-medium text-gray-800">Reddit Filters</h2>
        <div className="w-full sm:w-auto">
          <SearchComponent
            placeholder="Search in Reddit results..."
            initialValue={searchInputValue}
            onSearch={setSearchInputValue}
            showFilters={false}
          />
        </div>
        <div className="text-sm text-gray-500">
          Showing {filterOpportunities(opportunities).length} of {opportunities.length} opportunities
        </div>
      </div>
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Intent Level</h3>
          <div className="flex flex-wrap gap-2">
            <FilterButton label="All" onClick={() => setFilter("All")} isActive={filter === "All"} />
            <FilterButton label="High Intent" onClick={() => setFilter("High Intent")} isActive={filter === "High Intent"} />
            <FilterButton label="Medium Intent" onClick={() => setFilter("Medium Intent")} isActive={filter === "Medium Intent"} />
            <FilterButton label="Low Intent" onClick={() => setFilter("Low Intent")} isActive={filter === "Low Intent"} />
          </div>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Time Period</h3>
          <div className="flex flex-wrap gap-2">
            <FilterButton label="All Time" onClick={() => setDateFilter("all")} isActive={dateFilter === "all"} />
            <FilterButton label="Last 24 hours" onClick={() => setDateFilter("24h")} isActive={dateFilter === "24h"} />
            <FilterButton label="Last 7 days" onClick={() => setDateFilter("7d")} isActive={dateFilter === "7d"} />
            <FilterButton label="Last 30 days" onClick={() => setDateFilter("30d")} isActive={dateFilter === "30d"} />
          </div>
        </div>
      </div>
    </div>
  );

  // Twitter filter section component
  const TwitterFilterSection = () => (
    <div className="bg-white shadow-sm rounded-lg p-4 mb-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 space-y-3 sm:space-y-0">
        <h2 className="text-lg font-medium text-gray-800">Twitter Filters</h2>

        <div className="w-full sm:w-auto relative">
          <SearchComponent
            placeholder="Search in Twitter results..."
            initialValue={searchQuery}
            onSearch={setSearchQuery}
            showFilters={false}
          />
        </div>

        <div className="text-sm text-gray-500">
          Showing {filterTwitterLeads().length} of {twitterLeads.length} tweets
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Intent Level</h3>
          <div className="flex flex-wrap gap-2">
            <FilterButton label="All" onClick={() => setTwitterFilter("All")} isActive={twitterFilter === "All"} />
            <FilterButton label="High Intent" onClick={() => setTwitterFilter("High Intent")} isActive={twitterFilter === "High Intent"} />
            <FilterButton
              label="Medium Intent"
              onClick={() => setTwitterFilter("Medium Intent")}
              isActive={twitterFilter === "Medium Intent"}
            />
            <FilterButton label="Low Intent" onClick={() => setTwitterFilter("Low Intent")} isActive={twitterFilter === "Low Intent"} />
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Engagement</h3>
          <div className="flex flex-wrap gap-2">
            <FilterButton label="All Engagements" onClick={() => setEngagementFilter("All")} isActive={engagementFilter === "All"} />
            <FilterButton label="High Engagement" onClick={() => setEngagementFilter("High")} isActive={engagementFilter === "High"} />
            <FilterButton
              label="Medium Engagement"
              onClick={() => setEngagementFilter("Medium")}
              isActive={engagementFilter === "Medium"}
            />
            <FilterButton label="Low Engagement" onClick={() => setEngagementFilter("Low")} isActive={engagementFilter === "Low"} />
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Account Type</h3>
          <div className="flex items-center">
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={verifiedFilter}
                onChange={() => setVerifiedFilter(!verifiedFilter)}
                className="form-checkbox h-4 w-4 text-blue-500 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Only Verified Accounts</span>
            </label>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Time Period</h3>
          <div className="flex flex-wrap gap-2">
            <FilterButton label="All Time" onClick={() => setDateFilter("all")} isActive={dateFilter === "all"} />
            <FilterButton label="Last 24 hours" onClick={() => setDateFilter("24h")} isActive={dateFilter === "24h"} />
            <FilterButton label="Last 7 days" onClick={() => setDateFilter("7d")} isActive={dateFilter === "7d"} />
            <FilterButton label="Last 30 days" onClick={() => setDateFilter("30d")} isActive={dateFilter === "30d"} />
          </div>
        </div>
      </div>
    </div>
  );

  const EngagedLeadsFilterSection = () => (
    <div className="bg-white shadow-sm rounded-lg p-4 mb-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 space-y-3 sm:space-y-0">
        <h2 className="text-lg font-medium text-gray-800">Engaged Leads</h2>
        <div className="w-full sm:w-auto">
          <SearchComponent
            placeholder="Search in engaged leads..."
            initialValue={engagedSearchQuery} // You would need to add this state
            onSearch={setEngagedSearchQuery} // You would need to add this state and filter function
            showFilters={false}
          />
        </div>
        <div className="text-sm text-gray-500">{engagedLeads.length} engaged leads</div>
      </div>
    </div>
  );
  // Reddit opportunities tab
  // const OpportunitiesTab = React.memo(
  //   ({
  //     // Data
  //     opportunities, // Array of raw opportunity data
  //     products, // Array of product data for ProductList

  //     // Filter state and functions
  //     filter,
  //     setFilter,
  //     dateFilter,
  //     setDateFilter,
  //     searchInputValue,
  //     setSearchInputValue,
  //     filterOpportunities, // The memoized filter function from parent

  //     // Product selection and interaction
  //     selectedProduct,
  //     setSelectedProduct,
  //     fetchUserPromotionPosts, // Potentially called by ProductList/Card onClick
  //     fetchTwitterLeads, // Potentially called by ProductList/Card onClick
  //     setShowModal, // For Add/Edit button in ProductList

  //     // Card interaction state/functions
  //     expandedCard,
  //     toggleCard,
  //     togglePostCompletion,

  //     // Lead fetching state/functions
  //     fetchRedditAnalysis,
  //     isRefreshing,
  //     isAnalysisInProgress,
  //     credits,
  //   }) => {
  //     // Memoize the *result* of the filtering operation.
  //     // This recalculates *only* when the raw 'opportunities' array changes
  //     // OR when the 'filterOpportunities' function reference changes (which happens
  //     // when its own dependencies like filter, searchQuery, dateFilterStartTimestamp change).
  //     const filteredOps = useMemo(() => {
  //       console.log("Filtering opportunities in OpportunitiesTab useMemo...");
  //       return filterOpportunities(opportunities);
  //     }, [opportunities, filterOpportunities]);

  //     return (
  //       <div>
  //         {/* Filter Section: Pass down all necessary props */}
  //         <RedditFilterSection
  //           filter={filter}
  //           setFilter={setFilter}
  //           dateFilter={dateFilter}
  //           setDateFilter={setDateFilter}
  //           searchInputValue={searchInputValue}
  //           setSearchInputValue={setSearchInputValue}
  //           filteredCount={filteredOps.length} // Provide the calculated count
  //           totalCount={opportunities.length} // Provide the total count
  //         />

  //         {/* Main Content Grid */}
  //         <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
  //           {/* Left Column: Product List */}
  //           <div className="lg:col-span-1">
  //             <ProductList
  //               products={products}
  //               selectedProduct={selectedProduct}
  //               setSelectedProduct={setSelectedProduct}
  //               fetchUserPromotionPosts={fetchUserPromotionPosts} // Pass if needed
  //               fetchTwitterLeads={fetchTwitterLeads} // Pass if needed
  //               setShowModal={setShowModal} // Pass for add/edit button
  //             />
  //           </div>

  //           {/* Right Column: Filtered Opportunities */}
  //           <div className="lg:col-span-2">
  //             {/* Conditional Rendering: Show empty state or list */}
  //             {filteredOps.length === 0 ? (
  //               // Empty State UI
  //               <div className="bg-white rounded-lg shadow-sm p-8 text-center border border-gray-100">
  //                 <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
  //                   <Search className="w-8 h-8 text-gray-400" />
  //                 </div>
  //                 <h3 className="text-lg font-medium text-gray-800 mb-2">No Reddit opportunities found</h3>
  //                 <p className="text-gray-600 mb-4">Try adjusting your filters or fetch more leads from Reddit.</p>
  //                 {/* Button to trigger lead fetching */}
  //                 <button
  //                   onClick={fetchRedditAnalysis} // Action to find leads
  //                   disabled={isRefreshing || isAnalysisInProgress || !selectedProduct || credits < 1} // Disable conditions
  //                   className="flex items-center justify-center space-x-2 mx-auto px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
  //                   type="button"
  //                 >
  //                   {isRefreshing || isAnalysisInProgress ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
  //                   <span>{isRefreshing || isAnalysisInProgress ? "Analyzing..." : "Find Reddit Leads"}</span>
  //                 </button>
  //                 {!selectedProduct && <p className="text-xs text-red-500 mt-2">Please select a product first.</p>}
  //                 {credits < 1 && selectedProduct && <p className="text-xs text-red-500 mt-2">Not enough credits.</p>}
  //               </div>
  //             ) : (
  //               // List of Opportunity Cards
  //               <div className="space-y-4">
  //                 <AnimatePresence initial={false}>
  //                   {filteredOps.map((opportunity) => (
  //                     <OpportunityCard
  //                       key={opportunity.id}
  //                       opportunity={opportunity}
  //                       selectedProduct={selectedProduct} // Pass selected product for context in card
  //                       expandedCard={expandedCard} // Pass state for expansion
  //                       toggleCard={toggleCard} // Pass function to handle expansion
  //                       togglePostCompletion={togglePostCompletion} // Pass function for marking engaged
  //                     />
  //                   ))}
  //                 </AnimatePresence>
  //               </div>
  //             )}
  //           </div>
  //         </div>
  //       </div>
  //     );
  //   }
  // );

  const OpportunitiesTab = React.memo(
    ({
      // Data
      opportunities,
      products,

      // Filter state and functions/values
      filter,
      setFilter,
      platformFilter, // Assuming this state exists and is passed if used below
      dateFilter,
      setDateFilter,
      searchInputValue,
      setSearchInputValue,
      searchQuery, // Debounced search query
      dateFilterStartTimestamp, // Calculated start timestamp

      // Product selection and interaction
      selectedProduct,
      setSelectedProduct,
      fetchUserPromotionPosts,
      fetchTwitterLeads,
      setShowModal,

      // Card interaction state/functions
      expandedCard,
      toggleCard,
      togglePostCompletion,

      // Lead fetching state/functions
      fetchRedditAnalysis,
      isRefreshing,
      isAnalysisInProgress,
      credits,
    }) => {
      // Memoize the filtering result based on data and ACTUAL filter values
      // Inside OpportunitiesTab component

      const filteredOps = useMemo(() => {
        console.log("Filtering opportunities: useMemo run");
        const opportunitiesToFilter = opportunities || [];

        // Get filter values safely (provide defaults if undefined/null)
        const currentPlatformFilter = platformFilter ?? "All"; // Default to 'All' if undefined/null
        const currentSearchQuery = searchQuery ?? ""; // Default to empty string

        return opportunitiesToFilter.filter((opp) => {
          const details = opp.details || {}; // Ensure details object exists

          // --- Platform ---
          const platform = details.platform ?? ""; // Default platform to empty string if missing
          // Check against currentPlatformFilter safely
          const matchesPlatform = currentPlatformFilter === "All" || platform.toLowerCase() === currentPlatformFilter.toLowerCase();

          // --- Timestamp and Date Filter (Assumed okay from previous fixes) ---
          let timestamp = details.timestamp || opp.created_at;
          // ... (rest of timestamp logic) ...
          const oppTimestamp = timestamp ? new Date(timestamp).getTime() : 0;
          const matchesDate = !dateFilterStartTimestamp || (oppTimestamp && oppTimestamp >= dateFilterStartTimestamp);

          // --- Intent (Assumed okay) ---
          const score = Number(details.lead_quality_score) || 0;
          const matchesIntent =
            filter === "All" ||
            (filter === "High Intent" && score >= 80) ||
            (filter === "Medium Intent" && score >= 60 && score < 80) ||
            (filter === "Low Intent" && score < 60);

          // --- Search (Add safe checks) ---
          const lowerSearchQuery = currentSearchQuery.toLowerCase(); // Safe now because currentSearchQuery defaults to ""
          const postTitle = details.post_title ?? ""; // Default title to empty string
          const postExcerpt = details.post_excerpt ?? ""; // Default excerpt to empty string

          const matchesSearch =
            !currentSearchQuery || // Check if search query exists first
            postTitle.toLowerCase().includes(lowerSearchQuery) ||
            postExcerpt.toLowerCase().includes(lowerSearchQuery);

          return matchesIntent && matchesPlatform && matchesDate && matchesSearch;
        });
      }, [
        opportunities,
        filter,
        platformFilter, // Keep original prop dependency
        searchQuery, // Keep original prop dependency
        dateFilterStartTimestamp,
      ]); // Keep dependencies

      return (
        <div>
          {/* --- Filter Section --- */}
          <RedditFilterSection
            filter={filter}
            setFilter={setFilter}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            searchInputValue={searchInputValue}
            setSearchInputValue={setSearchInputValue}
            filteredCount={filteredOps.length}
            totalCount={opportunities?.length || 0} // Use optional chaining for safety
            // Pass platformFilter/setPlatformFilter if needed by RedditFilterSection
          />

          {/* --- Main Content Grid --- */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column: Product List */}
            <div className="lg:col-span-1">
              <ProductList
                products={products}
                selectedProduct={selectedProduct}
                setSelectedProduct={setSelectedProduct}
                fetchUserPromotionPosts={fetchUserPromotionPosts}
                fetchTwitterLeads={fetchTwitterLeads}
                setShowModal={setShowModal}
              />
            </div>

            {/* Right Column: Filtered Opportunities */}
            <div className="lg:col-span-2">
              {/* Check if still loading opportunities initially */}
              {/* Add a loading state from parent if needed */}
              {/* {isLoadingOpportunities ? <p>Loading...</p> : ( */}
              {filteredOps.length === 0 ? (
                // --- Empty State UI ---
                <div className="bg-white rounded-lg shadow-sm p-8 text-center border border-gray-100 min-h-[300px] flex flex-col justify-center items-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">No Reddit opportunities found</h3>
                  <p className="text-gray-600 mb-4 text-sm max-w-sm mx-auto">
                    Try adjusting your filters, checking your product keywords, or finding new leads.
                  </p>
                  <button
                    onClick={fetchRedditAnalysis}
                    disabled={isRefreshing || isAnalysisInProgress || !selectedProduct || credits < 1}
                    className="flex items-center justify-center space-x-2 mx-auto px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    type="button"
                  >
                    {isRefreshing || isAnalysisInProgress ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
                    <span>{isRefreshing || isAnalysisInProgress ? "Analyzing..." : "Find Reddit Leads"}</span>
                  </button>
                  {!selectedProduct && <p className="text-xs text-red-500 mt-2">Please select a product first.</p>}
                  {credits < 1 && selectedProduct && <p className="text-xs text-red-500 mt-2">Not enough credits.</p>}
                </div>
              ) : (
                // --- List of Opportunity Cards (Animated) ---
                <div className="space-y-4">
                  <AnimatePresence initial={false}>
                    {filteredOps.map((opportunity, index) => (
                      // <OpportunityCard
                      //   // Key is crucial for AnimatePresence and React list updates
                      //   key={opportunity.id}
                      //   opportunity={opportunity}
                      //   isPremiumUser={isPremium}
                      //   // Note: layout prop is now inside OpportunityCard
                      //   selectedProduct={selectedProduct}
                      //   expandedCard={expandedCard}
                      //   toggleCard={toggleCard}
                      //   togglePostCompletion={togglePostCompletion}
                      // />

                      <OpportunityCard
                        key={opportunity.id}
                        opportunity={opportunity}
                        selectedProduct={selectedProduct}
                        expandedCard={expandedCard}
                        toggleCard={toggleCard}
                        togglePostCompletion={togglePostCompletion}
                        isPremiumUser={isPremium} // Pass premium status from state
                        onUpgradeClick={handleUpgradeClick} // Pass the upgrade handler
                        leadIndex={index} // Pass the index to determine if it's a top lead
                      />
                    ))}
                  </AnimatePresence>
                </div>
              )}
              {/* )} */}
            </div>
          </div>
        </div>
      );
    }
  );

  const TwitterTab = () => {
    const filteredTwitterLeads = useMemo(
      () => filterTwitterLeads(),
      [twitterLeads, twitterFilter, engagementFilter, verifiedFilter, dateFilter, searchQuery]
    );

    return (
      <div>
        <TwitterFilterSection />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <ProductList />
          </div>
          <div className="lg:col-span-2">
            {filteredTwitterLeads.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Twitter className="w-8 h-8 text-blue-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">No Twitter leads found</h3>
                <p className="text-gray-600 mb-4">Click the "Find Twitter Leads" button to discover relevant Twitter conversations.</p>
                <button
                  onClick={fetchTwitterAnalysis}
                  disabled={isRefreshing || isTwitterAnalysisInProgress || credits < 1}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRefreshing ? "Analyzing..." : "Find Twitter Leads"}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredTwitterLeads.map((lead, index) => (
                  // <TwitterCard
                  //   key={lead.id}
                  //   lead={lead}
                  //   selectedProduct={selectedProduct}
                  //   expandedCard={expandedCard}
                  //   toggleCard={toggleCard}
                  //   togglePostCompletion={togglePostCompletion}
                  // />
                  <TwitterCard
                    key={lead.id}
                    lead={lead}
                    selectedProduct={selectedProduct}
                    expandedCard={expandedCard}
                    toggleCard={toggleCard}
                    togglePostCompletion={togglePostCompletion}
                    isPremiumUser={isPremium}
                    onUpgradeClick={handleUpgradeClick}
                    leadIndex={index}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // This component should be added to your GreenMarketingTab.js file
  const LinkedInTab = () => {
    const [linkedInFilter, setLinkedInFilter] = useState("All");
    const [companyFilter, setCompanyFilter] = useState("All");

    // Filter LinkedIn leads based on filter criteria
    const filteredLinkedInLeads = useMemo(() => {
      return linkedInLeads.filter((lead) => {
        const details = lead.details || {};

        // Intent filter
        const intentMatch =
          linkedInFilter === "All" ||
          (linkedInFilter === "High Intent" && details.lead_quality_score >= 80) ||
          (linkedInFilter === "Medium Intent" && details.lead_quality_score >= 60 && details.lead_quality_score < 80) ||
          (linkedInFilter === "Low Intent" && details.lead_quality_score < 60);

        // Account type filter
        const accountTypeMatch =
          companyFilter === "All" ||
          (companyFilter === "Company" && details.authorType === "Company") ||
          (companyFilter === "Person" && details.authorType === "Person");

        // Engagement filter
        const engagementScore = (details.like_count || 0) + (details.share_count || 0) + (details.comment_count || 0);
        const engagementMatch =
          engagementFilter === "All" ||
          (engagementFilter === "High" && engagementScore >= 100) ||
          (engagementFilter === "Medium" && engagementScore >= 30 && engagementScore < 100) ||
          (engagementFilter === "Low" && engagementScore < 30);

        // Date filter
        const timestamp = details.timestamp || lead.created_at;
        const postDate = new Date(timestamp);
        const now = new Date();
        const dateMatch =
          dateFilter === "all" ||
          (dateFilter === "24h" && now - postDate <= 24 * 60 * 60 * 1000) ||
          (dateFilter === "7d" && now - postDate <= 7 * 24 * 60 * 60 * 1000) ||
          (dateFilter === "30d" && now - postDate <= 30 * 24 * 60 * 60 * 1000);

        // Search query filter
        const searchMatch =
          !searchQuery ||
          (details.post_title && details.post_title.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (details.post_excerpt && details.post_excerpt.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (details.user_handle && details.user_handle.toLowerCase().includes(searchQuery.toLowerCase()));

        return intentMatch && accountTypeMatch && engagementMatch && dateMatch && searchMatch;
      });
    }, [linkedInLeads, linkedInFilter, companyFilter, engagementFilter, dateFilter, searchQuery]);

    return (
      <div>
        <LinkedInFilterSection
          linkedInFilter={linkedInFilter}
          setLinkedInFilter={setLinkedInFilter}
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          engagementFilter={engagementFilter}
          setEngagementFilter={setEngagementFilter}
          companyFilter={companyFilter}
          setCompanyFilter={setCompanyFilter}
          searchInputValue={searchInputValue}
          setSearchInputValue={setSearchInputValue}
          filteredCount={filteredLinkedInLeads.length}
          totalCount={linkedInLeads.length}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <ProductList />
          </div>
          <div className="lg:col-span-2">
            {isPremium ? (
              filteredLinkedInLeads.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Linkedin className="w-8 h-8 text-purple-500" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">No LinkedIn leads found</h3>
                  <p className="text-gray-600 mb-4">Click the "Find LinkedIn Leads" button to discover relevant LinkedIn conversations.</p>
                  <button
                    onClick={fetchLinkedInAnalysis}
                    disabled={isRefreshing || isLinkedInAnalysisInProgress || credits < 1}
                    className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRefreshing ? "Analyzing..." : "Find LinkedIn Leads"}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredLinkedInLeads.map((lead, index) => (
                    <LinkedInCard
                      key={lead.id}
                      lead={lead}
                      selectedProduct={selectedProduct}
                      expandedCard={expandedCard}
                      toggleCard={toggleCard}
                      togglePostCompletion={togglePostCompletion}
                      isPremiumUser={isPremium}
                      onUpgradeClick={handleUpgradeClick}
                      leadIndex={index}
                    />
                  ))}
                </div>
              )
            ) : (
              // Premium lock overlay for LinkedIn tab
              <div className="bg-white rounded-xl shadow-md p-8 text-center overflow-hidden relative">
                {/* Background design elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-purple-50 rounded-full -mr-32 -mt-32 opacity-30" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-50 rounded-full -ml-24 -mb-24 opacity-30" />

                <div className="relative z-10">
                  <div className="p-4 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-full mx-auto mb-6 w-20 h-20 flex items-center justify-center shadow-md">
                    <Linkedin className="w-10 h-10 text-purple-500" />
                  </div>

                  <div className="absolute top-6 right-6 transform rotate-12">
                    <div className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white text-sm font-bold rounded-lg shadow-lg">
                      PRO FEATURE
                    </div>
                  </div>

                  <h2 className="text-2xl font-bold text-gray-800 mb-3">LinkedIn Leads is a Premium Feature</h2>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Upgrade to unlock LinkedIn lead discovery and expand your reach to find high-quality professional leads.
                  </p>

                  <div className="flex justify-center mb-8">
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-100 max-w-md text-left">
                      <h3 className="font-medium text-purple-800 mb-2 flex items-center">
                        <Sparkles className="w-5 h-5 mr-2 text-purple-500" />
                        What you'll unlock with Premium
                      </h3>
                      <ul className="text-sm text-purple-700 space-y-2">
                        <li className="flex items-start">
                          <Check className="w-4 h-4 mr-2 text-purple-500 mt-0.5 flex-shrink-0" />
                          <span>Complete access to LinkedIn lead discovery</span>
                        </li>
                        <li className="flex items-start">
                          <Check className="w-4 h-4 mr-2 text-purple-500 mt-0.5 flex-shrink-0" />
                          <span>Find decision makers and B2B opportunities</span>
                        </li>
                        <li className="flex items-start">
                          <Check className="w-4 h-4 mr-2 text-purple-500 mt-0.5 flex-shrink-0" />
                          <span>Connect with industry-specific professionals</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <button
                    onClick={handleUpgradeClick}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium flex items-center mx-auto hover:shadow-lg transition-all duration-200 hover:scale-105 shadow-md"
                  >
                    <Zap className="w-5 h-5 mr-2" />
                    Upgrade Now
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Engaged leads tab
  // const EngagedLeadsTab = () => (
  //   <div>
  //     <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
  //       <div className="lg:col-span-1">
  //         <ProductList />
  //       </div>

  //       <div className="lg:col-span-2">
  //         {engagedLeads.length === 0 ? (
  //           <div className="bg-white rounded-lg shadow-sm p-8 text-center">
  //             <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
  //               <UserCheck className="w-8 h-8 text-gray-400" />
  //             </div>
  //             <h3 className="text-lg font-medium text-gray-800 mb-2">No engaged leads yet</h3>
  //             <p className="text-gray-600 mb-4">When you mark opportunities as engaged, they will appear here for easy tracking.</p>
  //             <button
  //               onClick={(e) => {
  //                 e.preventDefault();
  //                 setActiveSubTab("opportunities");
  //               }}
  //               className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
  //               type="button"
  //             >
  //               View Opportunities
  //             </button>
  //           </div>
  //         ) : (
  //           <div className="space-y-4">
  //             {engagedLeads.map((lead) => (
  //               <EngagedLeadCard key={lead.id} lead={lead} />
  //             ))}
  //           </div>
  //         )}
  //       </div>
  //     </div>
  //   </div>
  // );

  const EngagedLeadsTab = () => {
    return (
      <div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <ProductList />
          </div>
          <div className="lg:col-span-2">
            {engagedLeads.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserCheck className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">No engaged leads yet</h3>
                <p className="text-gray-600 mb-4">When you mark opportunities as engaged, they will appear here for easy tracking.</p>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveSubTab("opportunities");
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                  type="button"
                >
                  View Opportunities
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {engagedLeads.map((lead) => (
                  <EngagedLeadCard key={lead.id} lead={lead} expandedCard={expandedCard} toggleCard={toggleCard} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Main component render
  return (
    // <div key={refreshKey} className="bg-gray-50 min-h-screen">
    <div className="bg-gray-50 min-h-screen">
      {/* Simple Header - Matching the dashboard page */}
      {/* <header className="h-16 border-b border-gray-100 bg-white shadow-sm flex items-center px-4 sm:px-8 sticky top-0 z-30">
        <div className="max-w-6xl w-full mx-auto flex justify-between items-center">
          <span className="font-semibold text-xl text-green-600 tracking-tight">FeedbackbyAI</span>
          <Link to="/dashboard/" className="text-gray-600 hover:text-green-600 transition-colors flex items-center text-sm sm:text-base">
            <ArrowRight className="h-4 w-4 mr-1 transform rotate-180" />
            Back to Dashboard
          </Link>
        </div>
      </header> */}
      <EnhancedHeader />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {showAlert && <AlertBanner message={alertMessage} type={alertType} onClose={() => setShowAlert(false)} />}
        {/* Platform Instructions Banner - This helps users immediately understand what to do */}
        {/* Main content */}
        {products.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <NavigationBar />
            {activeSubTab === "opportunities" ? (
              <OpportunitiesTab
                filterOpportunities={filterOpportunities}
                opportunities={opportunities}
                filter={filter}
                setFilter={setFilter}
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                searchInputValue={searchInputValue}
                setSearchInputValue={setSearchInputValue}
                products={products}
                selectedProduct={selectedProduct}
                setSelectedProduct={setSelectedProduct}
                fetchUserPromotionPosts={fetchUserPromotionPosts}
                fetchTwitterLeads={fetchTwitterLeads}
                setShowModal={setShowModal}
                expandedCard={expandedCard}
                toggleCard={toggleCard}
                togglePostCompletion={togglePostCompletion}
                fetchRedditAnalysis={fetchRedditAnalysis}
                isRefreshing={isRefreshing}
                isAnalysisInProgress={isAnalysisInProgress}
                credits={credits}
              />
            ) : activeSubTab === "twitter" ? (
              isPremium ? (
                <TwitterTab />
              ) : (
                // Premium lock overlay for Twitter tab
                <div className="bg-white rounded-xl shadow-md p-8 text-center overflow-hidden relative">
                  {/* Background design elements */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-purple-50 rounded-full -mr-32 -mt-32 opacity-30" />
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-50 rounded-full -ml-24 -mb-24 opacity-30" />

                  <div className="relative z-10">
                    <div className="p-4 bg-gradient-to-r from-purple-100 to-blue-100 rounded-full mx-auto mb-6 w-20 h-20 flex items-center justify-center shadow-md">
                      <Twitter className="w-10 h-10 text-blue-500" />
                    </div>

                    <div className="absolute top-6 right-6 transform rotate-12">
                      <div className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white text-sm font-bold rounded-lg shadow-lg">
                        PRO FEATURE
                      </div>
                    </div>

                    <h2 className="text-2xl font-bold text-gray-800 mb-3">Twitter Leads is a Premium Feature</h2>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      Upgrade to unlock Twitter lead discovery and expand your reach to find even more potential customers.
                    </p>

                    <div className="flex justify-center mb-8">
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-100 max-w-md text-left">
                        <h3 className="font-medium text-purple-800 mb-2 flex items-center">
                          <Sparkles className="w-5 h-5 mr-2 text-purple-500" />
                          What you'll unlock with Premium
                        </h3>
                        <ul className="text-sm text-purple-700 space-y-2">
                          <li className="flex items-start">
                            <Check className="w-4 h-4 mr-2 text-purple-500 mt-0.5 flex-shrink-0" />
                            <span>Complete access to Twitter lead discovery</span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 mr-2 text-purple-500 mt-0.5 flex-shrink-0" />
                            <span>All high-quality leads across platforms</span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 mr-2 text-purple-500 mt-0.5 flex-shrink-0" />
                            <span>Expanded keyword limits and filtering tools</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <button
                      onClick={handleUpgradeClick}
                      className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium flex items-center mx-auto hover:shadow-lg transition-all duration-200 hover:scale-105 shadow-md"
                    >
                      <Zap className="w-5 h-5 mr-2" />
                      Upgrade Now
                    </button>
                  </div>
                </div>
              )
            ) : activeSubTab === "linkedin" ? (
              isPremium ? (
                <LinkedInTab />
              ) : (
                // Premium lock overlay for LinkedIn tab
                <div className="bg-white rounded-xl shadow-md p-8 text-center overflow-hidden relative">
                  {/* Background design elements */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-purple-50 rounded-full -mr-32 -mt-32 opacity-30" />
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-50 rounded-full -ml-24 -mb-24 opacity-30" />

                  <div className="relative z-10">
                    <div className="p-4 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-full mx-auto mb-6 w-20 h-20 flex items-center justify-center shadow-md">
                      <Linkedin className="w-10 h-10 text-purple-500" />
                    </div>

                    <div className="absolute top-6 right-6 transform rotate-12">
                      <div className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white text-sm font-bold rounded-lg shadow-lg">
                        PRO FEATURE
                      </div>
                    </div>

                    <h2 className="text-2xl font-bold text-gray-800 mb-3">LinkedIn Leads is a Premium Feature</h2>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      Upgrade to unlock LinkedIn lead discovery and expand your reach to find high-quality professional leads.
                    </p>

                    <div className="flex justify-center mb-8">
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-100 max-w-md text-left">
                        <h3 className="font-medium text-purple-800 mb-2 flex items-center">
                          <Sparkles className="w-5 h-5 mr-2 text-purple-500" />
                          What you'll unlock with Premium
                        </h3>
                        <ul className="text-sm text-purple-700 space-y-2">
                          <li className="flex items-start">
                            <Check className="w-4 h-4 mr-2 text-purple-500 mt-0.5 flex-shrink-0" />
                            <span>Complete access to LinkedIn lead discovery</span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 mr-2 text-purple-500 mt-0.5 flex-shrink-0" />
                            <span>Find decision makers and B2B opportunities</span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 mr-2 text-purple-500 mt-0.5 flex-shrink-0" />
                            <span>Connect with industry-specific professionals</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <button
                      onClick={handleUpgradeClick}
                      className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium flex items-center mx-auto hover:shadow-lg transition-all duration-200 hover:scale-105 shadow-md"
                    >
                      <Zap className="w-5 h-5 mr-2" />
                      Upgrade Now
                    </button>
                  </div>
                </div>
              )
            ) : (
              <EngagedLeadsTab />
            )}
          </>
        )}{" "}
        {/* Modals */}
        <AnimatePresence mode="wait">
          {showModal && (
            <StableProductModal
              isOpen={showModal}
              onClose={() => setShowModal(false)}
              initialData={productDetails}
              onSave={handleSaveProduct}
              isEdit={!!selectedProduct}
              isSaving={isSaving}
              keywordLimit={userDetails?.keyword_limit || 3}
              userPlan={userPlan}
              handleUpgradeClick={handleUpgradeClick}
            />
          )}
          {/* {isAnalysisModalOpen && <AnalysisProgressModal />} */}
          {/* <AnimatePresence>
            {isAnalysisModalOpen && (
              <AnalysisProgressModal
                isOpen={isAnalysisModalOpen}
                progress={analysisProgress}
                platform={activeSubTab === "twitter" ? "twitter" : "reddit"}
                onClose={() => {
                  setIsAnalysisModalOpen(false);
                  setIsRefreshing(false);
                  setIsAnalysisInProgress(false);
                }}
              />
            )} */}

          {/* <AnalysisProgressModal
            isOpen={isAnalysisModalOpen}
            progress={analysisProgress}
            platform={activeSubTab === "twitter" ? "twitter" : "reddit"}
            newPostsCount={newPostsCount}
            remainingCredits={remainingCredits}
            onClose={() => {
              setIsAnalysisModalOpen(false);
              setIsRefreshing(false);
              setIsAnalysisInProgress(false);
            }}
            onRetry={() => {
              setAnalysisProgress(0);
              activeSubTab === "twitter" ? fetchTwitterAnalysis() : fetchRedditAnalysis();
            }}
            onEditKeywords={() => {
              setIsAnalysisModalOpen(false);
              setIsRefreshing(false);
              setIsAnalysisInProgress(false);
              openEditModal(selectedProduct);
            }}
          /> */}

          <AnalysisProgressModal
            isOpen={isAnalysisModalOpen}
            progress={analysisProgress}
            platform={activeSubTab === "twitter" ? "twitter" : activeSubTab === "linkedin" ? "linkedin" : "reddit"}
            newPostsCount={newPostsCount}
            remainingCredits={remainingCredits}
            onClose={() => {
              setIsAnalysisModalOpen(false);
              setIsRefreshing(false);
              setIsAnalysisInProgress(false);
              setIsLinkedInAnalysisInProgress(false); // Add this line
            }}
            onRetry={() => {
              setAnalysisProgress(0);
              if (activeSubTab === "twitter") {
                fetchTwitterAnalysis();
              } else if (activeSubTab === "linkedin") {
                fetchLinkedInAnalysis();
              } else {
                fetchRedditAnalysis();
              }
            }}
            onEditKeywords={() => {
              setIsAnalysisModalOpen(false);
              setIsRefreshing(false);
              setIsAnalysisInProgress(false);
              setIsLinkedInAnalysisInProgress(false); // Add this line
              openEditModal(selectedProduct);
            }}
          />

          {showUpgradeModal && (
            // <UpgradeModal />
            <UpgradeModal
              isOpen={showUpgradeModal}
              onClose={() => setShowUpgradeModal(false)}
              upgradeMessage={upgradeMessage}
              handleUpgradeClick={handleUpgradeClick}
            />
          )}
        </AnimatePresence>
        {/* Scroll to top button */}
        <AnimatePresence>
          {scrollToTop && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              onClick={handleScrollToTop}
              className="fixed bottom-8 right-8 bg-gradient-to-r from-green-500 to-blue-500 text-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
            >
              <ArrowUp size={20} />
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default GreenMarketingTab;
